/* eslint-disable react-hooks/exhaustive-deps */
/**
 * SPP-675-2 — Dynamic Overlapping Window Chunk Manager shell.
 * Merges up to 3 consecutive chunks into one Slate document; spacers for
 * far-off chunks. Stress scroll: keep Slate mounted; seam skeletons in spacers;
 * window merge only after scroll settles (incremental slides avoid remount).
 */
import { RichTextEditor } from 'components/shared/RichTextEditor/RichTextEditor';
import { TranscriptSkeleton } from 'components/shared/Skeleton/TranscriptSkeleton';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CustomElement } from 'types/slate-custom-types';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import {
  CHUNK_MIN_MEASURED_PX,
  CHUNK_PLACEHOLDER_ROW_PX,
  CHUNK_SCROLL_SETTLE_MS,
  CHUNK_DWCM_CATCHUP_MS,
  CHUNK_DWCM_SCROLL_COOLDOWN_MS,
  CHUNK_LOAD_SCROLL_COOLDOWN_MS,
  CHUNK_SEEK_WINDOW_MAX,
  CHUNK_SEAM_PEEK_ROWS,
  ActiveWindow,
  TranscriptBlock,
  TranscriptChunkMeta,
  buildChunkBands,
  calibrateSlotHeightsFromWindowMeasure,
  estimateChunkHeight,
  measureEditorBandInScrollRoot,
  resolveWindowAroundOrdinal,
  resolveWindowForViewport,
  cloneNodesForSlateEditor,
  mergedWindowHasDuplicateStarts,
  seamSkeletonRowCount,
  viewportOrdinalOutsideWindow,
  viewportNeedsWindowCatchUp,
  viewportSpacerIntersection,
  viewportSpacerIntersectionForBand,
  viewportCenterOrdinal,
  visibleSpacerFillPx,
  windowContentBand,
  windowBoundaryTriggers,
  resolveBoundarySkeletonOverlayPx,
  windowsEqual,
  isSingleChunkUpwardSlide,
} from 'utils/transcriptChunk.util';
import {
  chunkPerfLog,
  chunkSeekPerf,
  endChunkSeekPerf,
  transcriptChunkStore,
  TranscriptChunkStoreSnapshot,
} from './TranscriptChunkStore';
import { syncPlaybackHighlightAfterChunkReady, syncBootTranscriptPresentation, getPlaybackActiveCaptionStart, playbackDataAttr, extendVideoSeekIgnore, isLinePlaySeekGuardActive, clearProgressBarSeekDriving, tryResumePlaybackAfterChunkReady } from './playbackHighlight';
import {
  catchUpWindowForViewport,
  spacerHeightAfter,
  spacerHeightBefore,
  WindowChangeInfo,
} from './useBidirectionalScrollWindow';
import { useTranscriptChunkLoadingCue, resolveTranscriptInteractionLocked, resolveDwcmTranscriptWaitLocked, resolveTranscriptLoadingCue, resolveShowViewportProcessingOverlay, resolvePlaybackSeekReadyToFinalize, syncTranscriptWheelLockCursor, resolveTranscriptScrollRootEl } from './useTranscriptChunkLoadingCue';
import { TranscriptViewportLoadingOverlay } from './TranscriptViewportLoadingOverlay';

type Props = {
  fullDocument: any[] | null | undefined;
  videoTimePosition: number;
  documentKey?: string | null;
};

const resolveScrollRoot = (): HTMLElement | null =>
  document.querySelector(
    '[data-transcript-scroll-root]',
  ) as HTMLElement | null;

/** Pointer down on the native scrollbar track — not wheel and not progress bar. */
const isTranscriptScrollbarPointer = (
  root: HTMLElement,
  clientX: number,
): boolean => {
  const scrollbarWidth = root.offsetWidth - root.clientWidth;
  if (scrollbarWidth <= 0) return false;
  const rect = root.getBoundingClientRect();
  return clientX >= rect.right - scrollbarWidth - 2;
};

/** Drop browser + Slate selection before chunk/window remount (avoids DOM point errors). */
const clearTranscriptEditorSelection = () => {
  try {
    const editor = (window as any).Editor;
    if (editor) {
      ReactEditor.deselect(editor);
      Transforms.deselect(editor);
    }
  } catch {
    // ignore — editor may already be torn down
  }
  try {
    window.getSelection()?.removeAllRanges();
  } catch {
    // ignore
  }
};

/** Remount Slate when the merged window changes; signal ready after paint. */
const DwcmBoundEditor = ({
  slateMountKey,
  windowKey,
  document,
  videoTimePosition,
  setDocument,
  interactionLocked,
  onWindowReady,
}: {
  slateMountKey: string;
  windowKey: string;
  document: CustomElement[];
  videoTimePosition: number;
  setDocument: (nodes: CustomElement[]) => void;
  interactionLocked?: boolean;
  onWindowReady?: () => void;
}) => {
  useLayoutEffect(() => {
    const w = transcriptChunkStore.getActiveWindow();
    if (!w || `${w.startOrdinal}-${w.endOrdinal}` !== windowKey) return;

    transcriptChunkStore.setEditorBindingForWindow(w);
    const t0 = performance.now();
    chunkPerfLog('dwcm:slate-mount-start', { windowKey, blocks: document.length });

    const accepted = transcriptChunkStore.markEditorWindowReady(w);
    const paintApproxMs = Number((performance.now() - t0).toFixed(1));
    chunkPerfLog('dwcm:slate-mount-ready', {
      windowKey,
      paintApproxMs,
      accepted,
    });
    if (!accepted) {
      // S5: a newer scroll/seek target superseded this mount — keep skeleton; do not finalize.
      chunkPerfLog('dwcm:slate-mount-superseded', { windowKey });
      return;
    }
    if (transcriptChunkStore.isPlaybackSeekInProgress()) {
      chunkSeekPerf('slate-ready', {
        windowKey,
        blocks: document.length,
        paintApproxMs,
      });
    }
    onWindowReady?.();

    const raf = requestAnimationFrame(() => {
      const root = resolveScrollRoot();
      const bootWindow = w.startOrdinal === 0;
      if (
        root &&
        bootWindow &&
        root.scrollTop < 64 &&
        !transcriptChunkStore.isUserScrollNavigationActive()
      ) {
        transcriptChunkStore.markProgrammaticScroll(250);
        root.scrollTop = 0;
      }

      if (
        !transcriptChunkStore.isFindJumpInProgress() &&
        !transcriptChunkStore.isUserScrollNavigationActive() &&
        !transcriptChunkStore.getScrollPinnedChunkId()
      ) {
        const time = window.Video?.currentTime ?? 0;
        const useBootPresentation =
          bootWindow &&
          (time <= 0.05 || transcriptChunkStore.isBeforeFirstCaptionTime(time));
        const afterSeek =
          transcriptChunkStore.consumeChunkReadyHighlightAfterSeek();
        if (useBootPresentation) {
          syncBootTranscriptPresentation(time);
        } else {
          syncPlaybackHighlightAfterChunkReady(
            afterSeek ? { afterSeek: true } : undefined,
          );
        }
      } else {
        transcriptChunkStore.consumeChunkReadyHighlightAfterSeek();
      }

      const chunkId = transcriptChunkStore.getActiveChunkId();
      if (chunkId && !transcriptChunkStore.isFindJumpInProgress()) {
        tryResumePlaybackAfterChunkReady();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [windowKey, slateMountKey, onWindowReady]);

  return (
    <RichTextEditor
      key={slateMountKey}
      document={document}
      videoTimePosition={videoTimePosition}
      setDocument={setDocument}
      interactionLocked={interactionLocked}
    />
  );
};

export const DwcmChunkedEditorShell = React.memo<Props>(
  ({ fullDocument, videoTimePosition, documentKey }) => {
    const [snapshot, setSnapshot] = useState<TranscriptChunkStoreSnapshot>(
      () => transcriptChunkStore.getSnapshot(),
    );
    const [editorDocument, setEditorDocument] = useState<any[]>(() => {
      if (
        Array.isArray(fullDocument) &&
        !isEmpty(fullDocument) &&
        transcriptChunkStore.matchesDocument(fullDocument, documentKey)
      ) {
        const merged = transcriptChunkStore.getMergedWindowNodes();
        return merged.length ? merged : (fullDocument as any[]);
      }
      return [];
    });
    const [wheelBlockActive, setWheelBlockActive] = useState(false);
    const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
    const [layoutTick, setLayoutTick] = useState(0);
    const [viewportPx, setViewportPx] = useState(0);
    const [viewportOutsideWindow, setViewportOutsideWindow] = useState(false);
    const [viewportSpacers, setViewportSpacers] = useState({
      top: false,
      bottom: false,
    });
    const [scrollMetrics, setScrollMetrics] = useState({
      scrollTop: 0,
      clientHeight: 0,
    });
    const [playbackSeekLoading, setPlaybackSeekLoading] = useState(false);
    const [editorDocGeneration, setEditorDocGeneration] = useState(0);
    const [scrollFrozen, setScrollFrozen] = useState(false);
    const [allowEditorMount, setAllowEditorMount] = useState(true);
    const [stressScrollPending, setStressScrollPending] = useState(false);
    /**
     * Viewport-anchored loading cue from first freeze / window change until
     * Slate has painted. In-slot spacers sit far off-screen after settle.
     */
    const [scrollLoading, setScrollLoading] = useState(
      () => !transcriptChunkStore.isEditorReady(),
    );

    /** Track B: sync presentation pipeline flags (no cleanup reset — avoids playable flicker). */
    useEffect(() => {
      if (!transcriptChunkStore.isEnabled()) return;
      transcriptChunkStore.setUxPipelineGuards({
        scrollLoading,
        stressScrollPending,
        scrollFrozen,
      });
    }, [scrollLoading, stressScrollPending, scrollFrozen]);

    const slotHeightRef = useRef<Record<string, number>>({});
    /** Per-block height learned from measured Slate; applied to unseen chunks. */
    const rowPxRef = useRef<number>(CHUNK_PLACEHOLDER_ROW_PX);
    const lastWindowKeyRef = useRef('');
    if (!lastWindowKeyRef.current) {
      const bootWindow = transcriptChunkStore.getActiveWindow();
      if (bootWindow) {
        lastWindowKeyRef.current = `${bootWindow.startOrdinal}-${bootWindow.endOrdinal}`;
      }
    }
    const lastPlaybackSeekSnapRef = useRef<string | null>(null);
    /** One window load per progress-bar release — store emits must not re-settle. */
    const lastSeekSettledRef = useRef<string | null>(null);
    /** Re-anchor scroll once after seek editor paints (estimated → live heights). */
    const pendingSeekScrollRef = useRef<{ chunkId: string; time: number } | null>(
      null,
    );
    /** Re-anchor scroll after stress-scroll settle (estimated slide delta → live bands). */
    const pendingSettleScrollRef = useRef<{
      scrollTop: number;
      centerOrdinal: number;
      clientHeight: number;
    } | null>(null);
    /** Scroll position to hold while a window remount is in flight (!editorReady). */
    const loadScrollAnchorRef = useRef<number | null>(null);
    /** Scroll pane — wheel-block marker only (scrollbar remains usable). */
    const scrollPaneLockedRef = useRef(false);
    /** Block wheel/touch while chunk window load is in flight — not scrollbar or progress bar. */
    const scrollLoadLockRef = useRef(false);
    const postLoadCooldownTimerRef = useRef(0);
    /** True while user is dragging the transcript scrollbar (not wheel). */
    const scrollbarDragActiveRef = useRef(false);
    /** User transcript scroll wins over in-flight progress-bar seek until scroll settles. */
    const userScrollOverridesSeekRef = useRef(false);
    const seekBandsCacheRef = useRef<ReturnType<typeof buildChunkBands> | null>(
      null,
    );
    const seekBandsCacheKeyRef = useRef('');
    const computeSeekScrollTopRef = useRef<
      (
        root: HTMLElement,
        target: { chunkId: string; time: number },
      ) => number | null
    >(() => null);
    const editorWindowRef = useRef<HTMLDivElement | null>(null);
    const scrollIdleRef = useRef(true);
    const scrollFrozenRef = useRef(false);
    const ignoreScrollCatchUpRef = useRef(false);
    const measureIdleTimerRef = useRef(0);
    const catchUpRetryTimerRef = useRef(0);
    const catchUpInFlightRef = useRef(false);
    const playbackSeekLoadingRef = useRef(false);
    const finishPlaybackSeekPresentationRef = useRef<(() => void) | null>(
      null,
    );
    const seekRootRetryRef = useRef(0);
    /** Suppress catch-up / overlay flicker while seek presentation settles. */
    const postSeekGraceUntilRef = useRef(0);
    /** Avoid re-running idle seek cleanup setState on every store emit. */
    const seekIdleCleanupAppliedRef = useRef(true);
    /** One finalize per editor mount — onWindowReady + layout effects share this path. */
    const seekPresentationFinalizedRef = useRef('');
    /** Last consumed in-window progress-bar seek generation. */
    const lastInWindowSeekGenRef = useRef(
      transcriptChunkStore.getInWindowSeekGeneration(),
    );
    /** Skip seam catch-up remount after a seek that already sat inside W. */
    const suppressViewportCatchUpRef = useRef(false);
    const inWindowSeekCatchUpTimerRef = useRef(0);
    const findJumpCatchUpTimerRef = useRef(0);
    /** Full scroll block (wheel + scrollbar + pin) while boundary/spacer chunk load runs. */
    const chunkScrollHardLockRef = useRef(false);
    /** Viewport overlay active — blocks pointer on content pane during remount. */
    const transcriptScrollBlockedRef = useRef(false);
    const snapshotSyncKeyRef = useRef('');
    const scrollViewportRafRef = useRef(0);
    const lastDocRevisionRef = useRef(-1);
    /** Suppress subscribe-driven remount while initFromFullDocument emit() is in flight. */
    const initInFlightRef = useRef(false);
    /** First Slate paint in this shell session — never seed from store (clear() sets ready=true). */
    const editorEverReadyRef = useRef(false);
    const indexRef = useRef(snapshot.index);
    indexRef.current = snapshot.index;

    const heightFor = useCallback(
      (meta: TranscriptChunkMeta) =>
        slotHeightRef.current[meta.chunkId] ??
        estimateChunkHeight(meta.blockCount, rowPxRef.current),
      [layoutTick],
    );

    const loadScrollAnchor = useCallback(
      () =>
        pendingSettleScrollRef.current?.scrollTop ??
        loadScrollAnchorRef.current,
      [],
    );

    /** Block all scroll (wheel, scrollbar, keyboard) while a chunk load owns the viewport. */
    const shouldHardLockScroll = useCallback(() => {
      if (transcriptChunkStore.isPlaybackSeekInProgress()) return false;
      if (transcriptChunkStore.isPlaybackSeekSliding()) return false;
      return editorEverReadyRef.current && scrollLoadLockRef.current;
    }, []);

    /** Block wheel/touch/keyboard during chunk load pipeline. */
    const shouldBlockWheelDuringWindowLoad = useCallback(() => {
      return shouldHardLockScroll();
    }, [shouldHardLockScroll]);

    /** Sync QA marker for wheel-only block (does not disable scrollbar). */
    const syncWheelLoadBlock = useCallback(() => {
      const root = scrollRoot ?? resolveScrollRoot();
      if (!root) return;

      const blocked = shouldBlockWheelDuringWindowLoad();
      if (blocked === scrollPaneLockedRef.current) return;
      scrollPaneLockedRef.current = blocked;

      if (blocked) {
        syncTranscriptWheelLockCursor(root, true);
        chunkPerfLog('dwcm:wheel-block', {
          scrollTop: Number(root.scrollTop.toFixed(1)),
          anchor: loadScrollAnchor(),
          editorReady: transcriptChunkStore.isEditorReady(),
        });
      } else {
        syncTranscriptWheelLockCursor(root, false);
        if (!scrollLoadLockRef.current) {
          setWheelBlockActive(false);
        }
        chunkPerfLog('dwcm:wheel-unblock', {
          scrollTop: Number(root.scrollTop.toFixed(1)),
        });
      }
    }, [scrollRoot, shouldBlockWheelDuringWindowLoad, loadScrollAnchor]);

    /** Drop boundary wheel/settle locks — progress-bar seek supersedes chunk seam load. */
    const clearBoundaryScrollLoadState = useCallback(
      (opts?: { keepScrollLoading?: boolean }) => {
        pendingSettleScrollRef.current = null;
        scrollLoadLockRef.current = false;
        loadScrollAnchorRef.current = null;
        scrollPaneLockedRef.current = false;
        setWheelBlockActive(false);
        if (!opts?.keepScrollLoading) {
          setScrollLoading(false);
        }
        setStressScrollPending(false);
        scrollFrozenRef.current = false;
        setScrollFrozen(false);
        transcriptChunkStore.setScrolling(false);
        syncWheelLoadBlock();
        const root = resolveTranscriptScrollRootEl();
        if (root) {
          syncTranscriptWheelLockCursor(root, false);
        }
      },
      [syncWheelLoadBlock],
    );

    /**
     * Record trigger-time scroll position and hard-lock all scroll until the target
     * chunk/window has painted (finishSettleScrollPresentation unblocks).
     */
    const armWheelBlockAtTrigger = useCallback(
      (root: HTMLElement) => {
        if (!editorEverReadyRef.current) return;
        if (transcriptChunkStore.isPlaybackSeekInProgress()) return;
        if (transcriptChunkStore.isPlaybackSeekSliding()) return;
        if (transcriptChunkStore.isFindJumpInProgress()) return;

        const triggerScrollTop =
          pendingSettleScrollRef.current?.scrollTop ?? root.scrollTop;
        loadScrollAnchorRef.current = triggerScrollTop;
        scrollLoadLockRef.current = true;
        setWheelBlockActive(true);
        syncWheelLoadBlock();
        chunkPerfLog('dwcm:wheel-block-arm', {
          triggerScrollTop: Number(triggerScrollTop.toFixed(1)),
          editorReady: transcriptChunkStore.isEditorReady(),
        });
      },
      [syncWheelLoadBlock],
    );

    /** Latest scroll metrics while the user is still moving — not used for chunk load. */
    const liveScrollDraftRef = useRef<{ scrollTop: number; clientHeight: number } | null>(
      null,
    );

    const captureSettleScrollAnchor = useCallback(
      (root: HTMLElement) => {
        const idx = indexRef.current;
        if (idx.length <= 1) return;
        const scrollTop = root.scrollTop;
        const clientHeight = root.clientHeight;
        pendingSettleScrollRef.current = {
          scrollTop,
          centerOrdinal: viewportCenterOrdinal(
            scrollTop,
            clientHeight,
            idx,
            heightFor,
          ),
          clientHeight,
        };
        loadScrollAnchorRef.current = scrollTop;
        liveScrollDraftRef.current = null;
        chunkPerfLog('dwcm:settle-anchor', {
          scrollTop: Number(scrollTop.toFixed(1)),
          centerOrdinal: pendingSettleScrollRef.current.centerOrdinal,
        });
      },
      [heightFor],
    );

    /** Commit final scroll position once per settle — never during in-flight scroll. */
    const recordFinalScrollOnSettle = useCallback(
      (root: HTMLElement) => {
        if (transcriptChunkStore.isProgrammaticScroll()) return;
        if (transcriptChunkStore.isPlaybackSeekInProgress()) return;
        if (transcriptChunkStore.isPlaybackSeekSliding()) return;
        captureSettleScrollAnchor(root);
        transcriptChunkStore.markUserScrollNavigation();
      },
      [captureSettleScrollAnchor],
    );

    const settleScrollTopOverride = () =>
      pendingSettleScrollRef.current?.scrollTop;

    const refreshEditorDocument = useCallback(
      (opts?: { remount?: boolean }) => {
        const w = transcriptChunkStore.getActiveWindow();
        if (!w) return;
        let merged = transcriptChunkStore.getMergedWindowNodes();
        const needsRepair = !transcriptChunkStore.isWindowMergeHealthy(w);
        if (needsRepair) {
          chunkPerfLog('dwcm:merge-needs-repair', {
            expected: transcriptChunkStore.expectedWindowBlockCount(w),
            actual: merged.length,
            window: w,
          });
          transcriptChunkStore.rematerializeWindowFromSource(w);
          merged = transcriptChunkStore.getMergedWindowNodes();
        }
        if (!merged.length) return;
        lastDocRevisionRef.current = transcriptChunkStore.getDocumentRevision();
        const cloned = cloneNodesForSlateEditor(merged);
        if (opts?.remount !== false) {
          setEditorDocGeneration((g) => g + 1);
        }
        setEditorDocument(cloned);
      },
      [],
    );

    /** Live merged-editor band — avoids false spacer hits from height estimates. */
    const getContentBand = useCallback((): { top: number; bottom: number } | null => {
      const w = transcriptChunkStore.getActiveWindow();
      const idx = indexRef.current;
      if (!w || !idx.length) return null;

      if (transcriptChunkStore.isEditorReady()) {
        const root = scrollRoot ?? resolveScrollRoot();
        const editorWindow = editorWindowRef.current;
        const editorEl = editorWindow?.querySelector(
          '[data-slate-editor]',
        ) as HTMLElement | null;
        if (root && editorWindow && editorEl) {
          const live = measureEditorBandInScrollRoot(root, editorWindow, editorEl);
          if (live) return live;
        }
      }

      return windowContentBand(idx, w, heightFor);
    }, [heightFor, layoutTick, scrollRoot]);

    /** Prefer live editor DOM band over cumulative estimates for outside/spacer checks. */
    const viewportOutsideForScroll = useCallback(
      (
        idx: TranscriptChunkMeta[],
        w: ActiveWindow,
        scrollTop: number,
        clientHeight: number,
      ) => {
        const band = getContentBand();
        if (band && transcriptChunkStore.isEditorReady()) {
          const viewMid = scrollTop + clientHeight * 0.35;
          if (viewMid >= band.top && viewMid <= band.bottom) return false;
        }
        return viewportOrdinalOutsideWindow(
          idx,
          w,
          scrollTop,
          clientHeight,
          heightFor,
        );
      },
      [heightFor, getContentBand],
    );

    const readSpacersForViewport = useCallback(
      (
        w: ActiveWindow,
        idx: TranscriptChunkMeta[],
        scrollTop: number,
        clientHeight: number,
      ) => {
        const band = getContentBand();
        if (band) {
          return viewportSpacerIntersectionForBand(
            scrollTop,
            clientHeight,
            band,
          );
        }
        return viewportSpacerIntersection(
          idx,
          w,
          scrollTop,
          clientHeight,
          heightFor,
        );
      },
      [getContentBand, heightFor],
    );

    const cancelActivePlaybackSeek = useCallback((reason: string) => {
      if (!transcriptChunkStore.isPlaybackSeekInProgress()) return false;
      transcriptChunkStore.cancelPlaybackSeek(reason);
      clearProgressBarSeekDriving();
      transcriptChunkStore.markUserScrollNavigation();
      setPlaybackSeekLoading(false);
      playbackSeekLoadingRef.current = false;
      pendingSeekScrollRef.current = null;
      lastSeekSettledRef.current = null;
      lastPlaybackSeekSnapRef.current = null;
      ignoreScrollCatchUpRef.current = false;
      clearBoundaryScrollLoadState();
      return true;
    }, [clearBoundaryScrollLoadState]);

    const applyWindowChange = useCallback(
      (_info: WindowChangeInfo) => {
        const w = transcriptChunkStore.getActiveWindow();
        const key = w ? `${w.startOrdinal}-${w.endOrdinal}` : '';
        if (!key || key === lastWindowKeyRef.current) return;

        const isInitialBind = lastWindowKeyRef.current === '';
        const prevKey = lastWindowKeyRef.current;
        const root = resolveScrollRoot();
        const seekJump = transcriptChunkStore.isPlaybackSeekInProgress();
        const findJump = transcriptChunkStore.isFindJumpInProgress();

        if (seekJump) {
          chunkSeekPerf('shell:remount-scheduled', {
            prevKey,
            nextKey: key,
            window: w,
          });
        }

        if (!isInitialBind) {
          clearTranscriptEditorSelection();
          // Window flush before remount happens in applyActiveWindowDirect while
          // editorReady and the outgoing window still match — never flush here
          // (store editorWindow is already the incoming W but Slate is stale).
          let incrementalSlide = false;
          if (root && w && prevKey.includes('-') && !seekJump && !findJump) {
            const [prevStartRaw, prevEndRaw] = prevKey.split('-');
            const prevStart = Number(prevStartRaw);
            const prevEnd = Number(prevEndRaw);
            const idx = indexRef.current;
            if (
              Number.isFinite(prevStart) &&
              Number.isFinite(prevEnd) &&
              idx.length > 1
            ) {
              const newStart = w.startOrdinal;
              const newEnd = w.endOrdinal;
              incrementalSlide =
                newStart >= prevStart - 1 &&
                newStart <= prevStart + 1 &&
                newEnd >= prevEnd - 1 &&
                newEnd <= prevEnd + 1;
              if (incrementalSlide && !pendingSettleScrollRef.current) {
                if (newStart > prevStart) {
                  let delta = 0;
                  for (let ord = prevStart; ord < newStart && ord < idx.length; ord++) {
                    delta += heightFor(idx[ord]);
                  }
                  if (delta > 0) {
                    transcriptChunkStore.markProgrammaticScroll(250);
                    root.scrollTop = Math.max(0, root.scrollTop + delta);
                  }
                } else if (newStart < prevStart) {
                  let delta = 0;
                  for (let ord = newStart; ord < prevStart && ord < idx.length; ord++) {
                    delta += heightFor(idx[ord]);
                  }
                  if (delta > 0) {
                    transcriptChunkStore.markProgrammaticScroll(250);
                    root.scrollTop = Math.max(0, root.scrollTop + delta);
                  }
                }
              }
            }
          }
          if (root) setViewportPx(root.clientHeight);
          const keepMounted = transcriptChunkStore.isEditorReady();
          const upwardPrepend =
            w != null &&
            Number.isFinite(Number(prevKey.split('-')[0])) &&
            w.startOrdinal < Number(prevKey.split('-')[0]);
          if (
            findJump ||
            (!seekJump && !keepMounted && !isLinePlaySeekGuardActive())
          ) {
            if (root) {
              armWheelBlockAtTrigger(root);
            }
            setScrollLoading(true);
          }
          if (findJump) {
            pendingSettleScrollRef.current = null;
            ignoreScrollCatchUpRef.current = true;
            suppressViewportCatchUpRef.current = true;
            if (findJumpCatchUpTimerRef.current) {
              window.clearInterval(findJumpCatchUpTimerRef.current);
            }
            findJumpCatchUpTimerRef.current = window.setInterval(() => {
              if (transcriptChunkStore.isFindJumpInProgress()) return;
              window.clearInterval(findJumpCatchUpTimerRef.current);
              findJumpCatchUpTimerRef.current = 0;
              window.setTimeout(() => {
                suppressViewportCatchUpRef.current = false;
                ignoreScrollCatchUpRef.current = false;
              }, CHUNK_DWCM_SCROLL_COOLDOWN_MS);
            }, 80);
            if (root) {
              const target = transcriptChunkStore.getFindJumpTarget();
              if (target) {
                const snapTop = computeSeekScrollTopRef.current(root, target);
                if (snapTop != null) {
                  transcriptChunkStore.markProgrammaticScroll(450);
                  root.scrollTop = snapTop;
                } else {
                  const meta = indexRef.current.find(
                    (m) => m.chunkId === target.chunkId,
                  );
                  if (meta) {
                    const bands = buildChunkBands(indexRef.current, heightFor);
                    const band = bands[meta.ordinal];
                    if (band) {
                      transcriptChunkStore.markProgrammaticScroll(450);
                      root.scrollTop = Math.max(
                        0,
                        band.offsetTop - root.clientHeight * 0.35,
                      );
                    }
                  }
                }
              }
            }
          }
          lastWindowKeyRef.current = key;
          refreshEditorDocument({
            remount:
              findJump || seekJump || upwardPrepend || !keepMounted,
          });
          return;
        }
        lastWindowKeyRef.current = key;
        refreshEditorDocument();
      },
      [refreshEditorDocument, heightFor, armWheelBlockAtTrigger],
    );

    useEffect(() => {
      return () => {
        if (findJumpCatchUpTimerRef.current) {
          window.clearInterval(findJumpCatchUpTimerRef.current);
          findJumpCatchUpTimerRef.current = 0;
        }
      };
    }, []);

    useEffect(() => {
      if (scrollFrozen) return;
      setAllowEditorMount(true);
    }, [scrollFrozen]);

    useEffect(() => {
      if (!snapshot.editorReady || !stressScrollPending) return;
      const root = scrollRoot ?? resolveScrollRoot();
      const w = snapshot.activeWindow;
      if (root && w && snapshot.index.length > 1) {
        const outside = viewportOutsideForScroll(
          snapshot.index,
          w,
          root.scrollTop,
          root.clientHeight,
        );
        if (outside) return;
      }
      setStressScrollPending(false);
      setViewportOutsideWindow(false);
      setViewportSpacers({ top: false, bottom: false });
    }, [
      snapshot.editorReady,
      stressScrollPending,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      snapshot.index,
      scrollRoot,
      heightFor,
      getContentBand,
      layoutTick,
    ]);

    /** Drop viewport cue once Slate has painted and viewport center is in-window. */
    useEffect(() => {
      if (!scrollLoading || scrollFrozen) return;
      if (!snapshot.editorReady || !allowEditorMount) return;

      const root = scrollRoot ?? resolveScrollRoot();
      const w = snapshot.activeWindow;
      if (
        root &&
        w &&
        snapshot.index.length > 1 &&
        !transcriptChunkStore.isFindJumpInProgress()
      ) {
        const outside = viewportOutsideForScroll(
          snapshot.index,
          w,
          root.scrollTop,
          root.clientHeight,
        );
        if (outside) return;
      }
      if (transcriptChunkStore.isPlaybackSeekInProgress()) {
        finishPlaybackSeekPresentationRef.current?.();
        return;
      }
      setScrollLoading(false);
    }, [
      scrollLoading,
      scrollFrozen,
      snapshot.editorReady,
      allowEditorMount,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      snapshot.index,
      scrollRoot,
      heightFor,
      layoutTick,
    ]);

    useEffect(() => {
      if (!snapshot.editorReady) return;
      editorEverReadyRef.current = true;
      if (scrollLoadLockRef.current) {
        if (!pendingSettleScrollRef.current) {
          scrollLoadLockRef.current = false;
          loadScrollAnchorRef.current = null;
          setWheelBlockActive(false);
          syncWheelLoadBlock();
        }
      } else if (!pendingSettleScrollRef.current) {
        loadScrollAnchorRef.current = null;
        syncWheelLoadBlock();
      }
      if (
        !transcriptChunkStore.isPlaybackSeekInProgress() &&
        !scrollLoadLockRef.current
      ) {
        setScrollLoading(false);
        setStressScrollPending(false);
        setViewportOutsideWindow(false);
        setViewportSpacers({ top: false, bottom: false });
      }
    }, [snapshot.editorReady, syncWheelLoadBlock]);

    /** Progress-bar seek finished — drop any stale boundary load locks still holding the overlay. */
    useLayoutEffect(() => {
      const editorReadyLive = transcriptChunkStore.isEditorReady();
      if (!editorReadyLive) return;
      if (transcriptChunkStore.isPlaybackSeekSliding()) return;

      const seekShellBusy =
        playbackSeekLoading ||
        playbackSeekLoadingRef.current ||
        !!transcriptChunkStore.getPlaybackSeekTarget() ||
        !!pendingSeekScrollRef.current;

      if (seekShellBusy) {
        finishPlaybackSeekPresentationRef.current?.();
        return;
      }

      transcriptChunkStore.healStalePlaybackSeekChrome();

      if (transcriptChunkStore.shouldSuppressPostSeekViewportCatchUp()) {
        if (scrollLoading || stressScrollPending || scrollFrozen) {
          setScrollLoading(false);
          setStressScrollPending(false);
          setScrollFrozen(false);
          scrollFrozenRef.current = false;
          transcriptChunkStore.setScrolling(false);
        }
      }

      if (
        wheelBlockActive ||
        scrollLoadLockRef.current ||
        scrollLoading ||
        stressScrollPending
      ) {
        clearBoundaryScrollLoadState();
        setViewportOutsideWindow(false);
        setViewportSpacers({ top: false, bottom: false });
      }
    }, [
      snapshot.editorReady,
      snapshot.playbackSeekInProgress,
      snapshot.revision,
      snapshot.inWindowSeekGeneration,
      playbackSeekLoading,
      wheelBlockActive,
      scrollLoading,
      stressScrollPending,
      clearBoundaryScrollLoadState,
      editorDocGeneration,
    ]);

    const syncSeekViewportStateAfterSeek = useCallback((root: HTMLElement) => {
      const w = transcriptChunkStore.getActiveWindow();
      const idx = indexRef.current;
      if (!w || idx.length <= 1) return;
      setScrollMetrics({
        scrollTop: root.scrollTop,
        clientHeight: root.clientHeight,
      });
      setViewportSpacers({ top: false, bottom: false });
      setViewportOutsideWindow(false);
      setLayoutTick((v) => v + 1);
    }, []);

    const finishPlaybackSeekPresentation = useCallback(() => {
      if (isLinePlaySeekGuardActive()) {
        setScrollLoading(false);
        setPlaybackSeekLoading(false);
        playbackSeekLoadingRef.current = false;
        clearBoundaryScrollLoadState();
        return;
      }
      if (transcriptChunkStore.isPlaybackSeekSliding()) {
        const slidingTarget = transcriptChunkStore.getPlaybackSeekTarget();
        if (!slidingTarget) {
          // In-window scrub — target stays null; clear once editor is ready.
          if (transcriptChunkStore.isEditorReady()) {
            transcriptChunkStore.setPlaybackSeekSliding(false);
          } else {
            return;
          }
        } else if (
          !transcriptChunkStore.isEditorReady() ||
          !transcriptChunkStore.isSeekSatisfiedAtTime(slidingTarget.time)
        ) {
          return;
        } else {
          transcriptChunkStore.setPlaybackSeekSliding(false);
        }
      }

      const pending = pendingSeekScrollRef.current;
      const target = transcriptChunkStore.getPlaybackSeekTarget();
      const resolved = target
        ? transcriptChunkStore.resolveChunkForTime(target.time, {
            playing: false,
          })
        : null;
      if (
        target &&
        !resolvePlaybackSeekReadyToFinalize({
          editorReady: transcriptChunkStore.isEditorReady(),
          target,
          window: transcriptChunkStore.getActiveWindow(),
          targetOrdinal: resolved?.meta.ordinal ?? null,
        })
      ) {
        return;
      }
      const shouldFinalize =
        !!pending ||
        playbackSeekLoadingRef.current ||
        transcriptChunkStore.isPlaybackSeekInProgress();
      if (!shouldFinalize) {
        // In-window seek already cleared store flags; still drop leftover
        // seek/scroll chrome so line-play does not wait for a user nudge.
        if (transcriptChunkStore.isEditorReady()) {
          setPlaybackSeekLoading(false);
          playbackSeekLoadingRef.current = false;
          setScrollLoading(false);
          clearBoundaryScrollLoadState();
        }
        return;
      }

      const w = transcriptChunkStore.getActiveWindow();
      const finalizeKey = w
        ? `${w.startOrdinal}-${w.endOrdinal}:${transcriptChunkStore.getEditorReadyRevision()}`
        : '';
      if (finalizeKey && seekPresentationFinalizedRef.current === finalizeKey) {
        return;
      }
      if (finalizeKey) {
        seekPresentationFinalizedRef.current = finalizeKey;
      }
      transcriptChunkStore.markChunkReadyHighlightAfterSeek();

      if (pending) {
        pendingSeekScrollRef.current = null;
        const root = scrollRoot ?? resolveScrollRoot();
        if (root) {
          const scrollTop = computeSeekScrollTopRef.current(root, pending);
          if (scrollTop != null) {
            transcriptChunkStore.markProgrammaticScroll(250);
            root.scrollTop = scrollTop;
            syncSeekViewportStateAfterSeek(root);
          } else {
            const meta = indexRef.current.find((m) => m.chunkId === pending.chunkId);
            if (meta) {
              const bands = buildChunkBands(indexRef.current, heightFor);
              const band = bands[meta.ordinal];
              if (band) {
                transcriptChunkStore.markProgrammaticScroll(250);
                root.scrollTop = Math.max(
                  0,
                  band.offsetTop - root.clientHeight * 0.35,
                );
                syncSeekViewportStateAfterSeek(root);
              }
            }
          }
          syncSeekViewportStateAfterSeek(root);
          requestAnimationFrame(() => {
            const captionStart = getPlaybackActiveCaptionStart();
            if (captionStart == null) return;
            const shell = document.querySelector(
              `div[data-slate-node="element"][data-start="${playbackDataAttr(captionStart)}"]`,
            ) as HTMLElement | null;
            if (!shell) return;
            const rootRect = root.getBoundingClientRect();
            const elRect = shell.getBoundingClientRect();
            const inViewport =
              elRect.bottom > rootRect.top && elRect.top < rootRect.bottom;
            if (inViewport) return;

            const w = transcriptChunkStore.getActiveWindow();
            const nearViewportStart =
              elRect.top >= rootRect.top - 4 &&
              elRect.top <= rootRect.top + 48;
            const useStart =
              w?.startOrdinal === 0 &&
              root.scrollTop < 64 &&
              nearViewportStart;
            const delta = useStart
              ? elRect.top - rootRect.top
              : elRect.top +
                elRect.height / 2 -
                (rootRect.top + rootRect.height / 2);
            if (Math.abs(delta) > 4) {
              transcriptChunkStore.markProgrammaticScroll(250);
              root.scrollTop += delta;
              syncSeekViewportStateAfterSeek(root);
            }
          });
        }
      }

      setPlaybackSeekLoading(false);
      playbackSeekLoadingRef.current = false;
      setScrollLoading(false);
      setViewportOutsideWindow(false);
      setViewportSpacers({ top: false, bottom: false });
      ignoreScrollCatchUpRef.current = true;
      postSeekGraceUntilRef.current =
        performance.now() + CHUNK_LOAD_SCROLL_COOLDOWN_MS;
      if (postLoadCooldownTimerRef.current) {
        window.clearTimeout(postLoadCooldownTimerRef.current);
      }
      postLoadCooldownTimerRef.current = window.setTimeout(() => {
        postLoadCooldownTimerRef.current = 0;
        postSeekGraceUntilRef.current = 0;
        if (
          !scrollLoadLockRef.current &&
          !transcriptChunkStore.isPlaybackSeekInProgress()
        ) {
          ignoreScrollCatchUpRef.current = false;
        }
        setLayoutTick((v) => v + 1);
      }, CHUNK_LOAD_SCROLL_COOLDOWN_MS);
      clearBoundaryScrollLoadState();
      const rootEl = scrollRoot ?? resolveScrollRoot();
      endChunkSeekPerf({
        chunkId: pending?.chunkId,
        time: pending?.time,
        scrollTop: rootEl?.scrollTop,
        window: transcriptChunkStore.getActiveWindow(),
      });
      seekIdleCleanupAppliedRef.current = true;
      transcriptChunkStore.releasePlaybackSeek();
      extendVideoSeekIgnore();
      clearProgressBarSeekDriving();
      transcriptChunkStore.clearUserScrollNavigation();
      transcriptChunkStore.clearScrollPinnedChunk();
      tryResumePlaybackAfterChunkReady();
      setLayoutTick((v) => v + 1);
    }, [scrollRoot, syncSeekViewportStateAfterSeek, heightFor, clearBoundaryScrollLoadState]);

    useEffect(() => {
      finishPlaybackSeekPresentationRef.current = finishPlaybackSeekPresentation;
    }, [finishPlaybackSeekPresentation]);

    const finishSettleScrollPresentation = useCallback(() => {
      if (transcriptChunkStore.isPlaybackSeekInProgress()) return;
      if (transcriptChunkStore.isFindJumpInProgress()) return;

      const pending = pendingSettleScrollRef.current;
      if (!pending) return;
      pendingSettleScrollRef.current = null;

      const root = scrollRoot ?? resolveScrollRoot();
      if (!root) return;

      const w = transcriptChunkStore.getActiveWindow();
      const idx = indexRef.current;

      // Prefer the captured settle anchor — never re-anchor from height estimates
      // (estimate drift causes snap-back and re-triggers boundary catch-up loops).
      let nextScrollTop = pending.scrollTop;
      if (w && idx.length > 1) {
        const outsideAtAnchor = viewportOutsideForScroll(
          idx,
          w,
          pending.scrollTop,
          pending.clientHeight,
        );
        if (!outsideAtAnchor) {
          nextScrollTop = pending.scrollTop;
        } else {
          const liveBand = getContentBand();
          if (liveBand) {
            const viewMid = pending.scrollTop + pending.clientHeight * 0.35;
            if (viewMid < liveBand.top) {
              nextScrollTop = Math.max(
                0,
                liveBand.top - pending.clientHeight * 0.15,
              );
            } else if (viewMid > liveBand.bottom) {
              nextScrollTop = Math.max(
                0,
                liveBand.bottom - pending.clientHeight * 0.85,
              );
            }
          }
        }
      }

      if (Math.abs(root.scrollTop - nextScrollTop) > 4) {
        transcriptChunkStore.markProgrammaticScroll(350);
        root.scrollTop = nextScrollTop;
        setScrollMetrics({
          scrollTop: nextScrollTop,
          clientHeight: root.clientHeight,
        });
      }

      setViewportOutsideWindow(false);
      setViewportSpacers({ top: false, bottom: false });
      setStressScrollPending(false);
      clearBoundaryScrollLoadState();
      loadScrollAnchorRef.current = null;

      ignoreScrollCatchUpRef.current = true;
      if (postLoadCooldownTimerRef.current) {
        window.clearTimeout(postLoadCooldownTimerRef.current);
      }
      postLoadCooldownTimerRef.current = window.setTimeout(() => {
        postLoadCooldownTimerRef.current = 0;
        if (!scrollLoadLockRef.current) {
          ignoreScrollCatchUpRef.current = false;
        }
      }, CHUNK_LOAD_SCROLL_COOLDOWN_MS);

      setLayoutTick((v) => v + 1);

      chunkPerfLog('dwcm:settle-restore', {
        anchorScrollTop: Number(pending.scrollTop.toFixed(1)),
        restoredScrollTop: Number(nextScrollTop.toFixed(1)),
        centerOrdinal: pending.centerOrdinal,
        window: transcriptChunkStore.getActiveWindow(),
      });
      syncWheelLoadBlock();
    }, [
      scrollRoot,
      heightFor,
      syncWheelLoadBlock,
      getContentBand,
      viewportOutsideForScroll,
      clearBoundaryScrollLoadState,
    ]);

    useEffect(() => {
      if (!snapshot.editorReady) return;
      if (!pendingSettleScrollRef.current) return;
      if (transcriptChunkStore.isPlaybackSeekInProgress()) return;
      if (transcriptChunkStore.isFindJumpInProgress()) return;
      finishSettleScrollPresentation();
    }, [
      snapshot.editorReady,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      finishSettleScrollPresentation,
    ]);

    /** Finalize seek once Slate is ready — must run while seek target is still set. */
    useLayoutEffect(() => {
      if (!transcriptChunkStore.isEditorReady()) return;
      if (transcriptChunkStore.isPlaybackSeekSliding()) return;
      if (
        !pendingSeekScrollRef.current &&
        !playbackSeekLoadingRef.current &&
        !transcriptChunkStore.isPlaybackSeekInProgress()
      ) {
        return;
      }
      finishPlaybackSeekPresentationRef.current?.();
    }, [
      snapshot.editorReady,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      playbackSeekLoading,
      editorDocGeneration,
    ]);

    /**
     * Heal seek deadlocks: Slate painted but store editorReady lagged, or seek
     * flags stuck after the target chunk is already on screen.
     */
    useLayoutEffect(() => {
      const seekActive =
        playbackSeekLoadingRef.current ||
        !!transcriptChunkStore.getPlaybackSeekTarget();
      if (!seekActive) return;

      const w = transcriptChunkStore.getActiveWindow();
      if (!w) return;

      const storeKey = `${w.startOrdinal}-${w.endOrdinal}`;
      const renderedKey = snapshot.activeWindow
        ? `${snapshot.activeWindow.startOrdinal}-${snapshot.activeWindow.endOrdinal}`
        : '';

      if (storeKey !== renderedKey) {
        if (snapshotSyncKeyRef.current !== storeKey) {
          snapshotSyncKeyRef.current = storeKey;
          setSnapshot(transcriptChunkStore.getSnapshot());
        }
        return;
      }
      snapshotSyncKeyRef.current = '';

      if (transcriptChunkStore.isFindJumpInProgress()) {
        return;
      }

      const editorEl = editorWindowRef.current?.querySelector(
        '[data-slate-editor]',
      ) as HTMLElement | null;

      const target = transcriptChunkStore.getPlaybackSeekTarget();
      const resolved = target
        ? transcriptChunkStore.resolveChunkForTime(target.time, {
            playing: false,
          })
        : null;
      const targetInMountedWindow = resolvePlaybackSeekReadyToFinalize({
        editorReady: true,
        target,
        window: w,
        targetOrdinal: resolved?.meta.ordinal ?? null,
      });

      if (
        targetInMountedWindow &&
        editorEl &&
        editorEl.offsetHeight >= CHUNK_MIN_MEASURED_PX &&
        !transcriptChunkStore.isEditorReady()
      ) {
        chunkPerfLog('seek:heal-editor-ready', {
          storeKey,
          blocks: editorEl.querySelectorAll('[data-slate-node="element"]').length,
        });
        transcriptChunkStore.markEditorWindowReady(w);
      }

      if (
        targetInMountedWindow &&
        transcriptChunkStore.isEditorReady()
      ) {
        finishPlaybackSeekPresentationRef.current?.();
      }
    }, [
      playbackSeekLoading,
      snapshot.editorReady,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      editorDocGeneration,
    ]);

    /**
     * Find-jump deadlock heal: Slate painted the match chunk but editorReady
     * stayed false (stale viewport-catch-up used to steal W; heal was skipped).
     */
    useLayoutEffect(() => {
      if (!transcriptChunkStore.isFindJumpInProgress()) return;

      const w = transcriptChunkStore.getActiveWindow();
      const target = transcriptChunkStore.getFindJumpTarget();
      if (!w || !target?.chunkId) return;

      const storeKey = `${w.startOrdinal}-${w.endOrdinal}`;
      const renderedKey = snapshot.activeWindow
        ? `${snapshot.activeWindow.startOrdinal}-${snapshot.activeWindow.endOrdinal}`
        : '';
      if (storeKey !== renderedKey) return;

      const meta = indexRef.current.find((m) => m.chunkId === target.chunkId);
      if (
        !meta ||
        meta.ordinal < w.startOrdinal ||
        meta.ordinal > w.endOrdinal
      ) {
        return;
      }

      const editorEl = editorWindowRef.current?.querySelector(
        '[data-slate-editor]',
      ) as HTMLElement | null;
      const painted =
        !!editorEl &&
        (editorEl.offsetHeight >= CHUNK_MIN_MEASURED_PX ||
          editorEl.querySelectorAll('[data-slate-node="element"]').length > 0);
      if (!painted || transcriptChunkStore.isEditorReady()) return;

      chunkPerfLog('find-jump:heal-editor-ready', {
        storeKey,
        chunkId: target.chunkId,
        blocks: editorEl.querySelectorAll('[data-slate-node="element"]').length,
      });
      transcriptChunkStore.markEditorWindowReady(w);
    }, [
      snapshot.editorReady,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      editorDocGeneration,
    ]);

    /** Safety net — remount or release if seek skeleton outlives the load. */
    useEffect(() => {
      if (
        !playbackSeekLoading &&
        !transcriptChunkStore.getPlaybackSeekTarget()
      ) {
        return;
      }
      const timer = window.setTimeout(() => {
        if (
          !playbackSeekLoadingRef.current &&
          !transcriptChunkStore.isPlaybackSeekInProgress()
        ) {
          return;
        }
        chunkPerfLog('seek:watchdog-force-finish', {
          editorReady: transcriptChunkStore.isEditorReady(),
          window: transcriptChunkStore.getActiveWindow(),
        });
        const target = transcriptChunkStore.getPlaybackSeekTarget();
        const resolved = target
          ? transcriptChunkStore.resolveChunkForTime(target.time, {
              playing: false,
            })
          : null;
        if (
          resolvePlaybackSeekReadyToFinalize({
            editorReady: transcriptChunkStore.isEditorReady(),
            target,
            window: transcriptChunkStore.getActiveWindow(),
            targetOrdinal: resolved?.meta.ordinal ?? null,
          })
        ) {
          finishPlaybackSeekPresentationRef.current?.();
          return;
        }
        refreshEditorDocument({ remount: true });
        requestAnimationFrame(() => {
          const w = transcriptChunkStore.getActiveWindow();
          const editorEl = editorWindowRef.current?.querySelector(
            '[data-slate-editor]',
          ) as HTMLElement | null;
          if (
            w &&
            editorEl &&
            editorEl.offsetHeight >= CHUNK_MIN_MEASURED_PX &&
            !transcriptChunkStore.isEditorReady()
          ) {
            transcriptChunkStore.markEditorWindowReady(w);
          }
          if (transcriptChunkStore.isEditorReady()) {
            finishPlaybackSeekPresentationRef.current?.();
            return;
          }
          transcriptChunkStore.cancelPlaybackSeek('watchdog');
          setPlaybackSeekLoading(false);
          playbackSeekLoadingRef.current = false;
          pendingSeekScrollRef.current = null;
          clearBoundaryScrollLoadState();
          setScrollLoading(false);
          setViewportOutsideWindow(false);
          setViewportSpacers({ top: false, bottom: false });
        });
      }, 800);
      return () => window.clearTimeout(timer);
    }, [
      playbackSeekLoading,
      snapshot.editorReady,
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
      clearBoundaryScrollLoadState,
      refreshEditorDocument,
    ]);

    useEffect(() => {
      playbackSeekLoadingRef.current = playbackSeekLoading;
    }, [playbackSeekLoading]);

    /** Progress-bar seek: snap while dragging; skeleton + window load on release. */
    useEffect(() => {
      if (snapshot.index.length <= 1) return;

      const heightForFn = (m: TranscriptChunkMeta) =>
        slotHeightRef.current[m.chunkId] ??
        estimateChunkHeight(m.blockCount, rowPxRef.current);

      const bandsForSeek = () => {
        const idx = indexRef.current;
        let heightKey = 0;
        for (const m of idx) {
          heightKey +=
            slotHeightRef.current[m.chunkId] ??
            estimateChunkHeight(m.blockCount, rowPxRef.current);
        }
        const key = `${idx.length}:${heightKey}`;
        if (seekBandsCacheKeyRef.current !== key) {
          seekBandsCacheKeyRef.current = key;
          seekBandsCacheRef.current = buildChunkBands(idx, heightForFn);
        }
        return seekBandsCacheRef.current ?? [];
      };

      const computeSeekScrollTop = (
        root: HTMLElement,
        target: { chunkId: string; time: number },
      ): number | null => {
        const meta = indexRef.current.find((m) => m.chunkId === target.chunkId);
        if (!meta) return null;

        const bands = bandsForSeek();
        const band = bands[meta.ordinal];
        if (!band) return null;
        const chunkTop = band.offsetTop;

        const nodes = transcriptChunkStore.getChunkNodes(meta.chunkId);
        let rowIndex = 0;
        for (let i = 0; i < nodes.length; i++) {
          const start = nodes[i]?.data?.start;
          if (typeof start === 'number' && start <= target.time) {
            rowIndex = i;
          } else if (typeof start === 'number' && start > target.time) {
            break;
          }
        }
        const rowPx =
          slotHeightRef.current[meta.chunkId] != null
            ? slotHeightRef.current[meta.chunkId] / Math.max(meta.blockCount, 1)
            : CHUNK_PLACEHOLDER_ROW_PX;
        const captionTop = chunkTop + rowIndex * rowPx;
        return Math.max(0, captionTop - root.clientHeight * 0.35);
      };

      computeSeekScrollTopRef.current = computeSeekScrollTop;

      const ensureSeekWindowForOrdinal = (
        ordinal: number,
        withNeighbors: boolean,
      ): boolean => {
        const w = transcriptChunkStore.getActiveWindow();
        if (
          w &&
          ordinal >= w.startOrdinal &&
          ordinal <= w.endOrdinal
        ) {
          return false;
        }
        const idxLen = indexRef.current.length;
        const targetWindow = withNeighbors
          ? resolveWindowAroundOrdinal(
              ordinal,
              idxLen,
              CHUNK_SEEK_WINDOW_MAX,
            )
          : { startOrdinal: ordinal, endOrdinal: ordinal };
        if (
          w &&
          w.startOrdinal === targetWindow.startOrdinal &&
          w.endOrdinal === targetWindow.endOrdinal
        ) {
          return false;
        }
        transcriptChunkStore.setActiveWindowDirect(
          targetWindow,
          withNeighbors ? 'playback-seek-window' : 'playback-seek-preview',
        );
        return true;
      };

      const tryFinishSeekIfEditorReady = () => {
        const target = transcriptChunkStore.getPlaybackSeekTarget();
        if (
          !target ||
          transcriptChunkStore.isPlaybackSeekSliding() ||
          !transcriptChunkStore.isEditorReady()
        ) {
          return;
        }
        const resolved = transcriptChunkStore.resolveChunkForTime(target.time, {
          playing: false,
        });
        const w = transcriptChunkStore.getActiveWindow();
        if (
          !resolved ||
          !w ||
          resolved.meta.ordinal < w.startOrdinal ||
          resolved.meta.ordinal > w.endOrdinal
        ) {
          return;
        }
        finishPlaybackSeekPresentationRef.current?.();
      };

      const settlePlaybackSeekWindow = (
        root: HTMLElement,
        target: { chunkId: string; time: number },
      ) => {
        const meta = indexRef.current.find((m) => m.chunkId === target.chunkId);
        if (!meta) {
          chunkSeekPerf('settle-window:missing-meta', { target });
          return;
        }

        clearBoundaryScrollLoadState();
        playbackSeekLoadingRef.current = true;
        seekIdleCleanupAppliedRef.current = false;
        setPlaybackSeekLoading(true);
        setViewportPx(root.clientHeight);

        const windowChanged = ensureSeekWindowForOrdinal(meta.ordinal, true);
        pendingSeekScrollRef.current = {
          chunkId: target.chunkId,
          time: target.time,
        };

        if (!windowChanged && !transcriptChunkStore.isEditorReady()) {
          refreshEditorDocument({ remount: true });
        }

        chunkSeekPerf('settle-window', {
          target,
          ordinal: meta.ordinal,
          windowChanged,
          window: transcriptChunkStore.getActiveWindow(),
          indexLength: indexRef.current.length,
        });

        if (!windowChanged) {
          tryFinishSeekIfEditorReady();
        }
      };

      const syncSeekViewportState = (root: HTMLElement) => {
        const w = transcriptChunkStore.getActiveWindow();
        const idx = indexRef.current;
        if (!w || idx.length <= 1) return;
        const scrollTop = root.scrollTop;
        const clientHeight = root.clientHeight;
        const outside = viewportOutsideForScroll(
          idx,
          w,
          scrollTop,
          clientHeight,
        );
        const spacers = readSpacersForViewport(
          w,
          idx,
          scrollTop,
          clientHeight,
        );
        setScrollMetrics({ scrollTop, clientHeight });
        setViewportSpacers(spacers);
        setViewportOutsideWindow(outside);
      };

      const snapToTarget = (
        root: HTMLElement,
        target: { chunkId: string; time: number },
        smooth: boolean,
      ) => {
        const scrollTop = computeSeekScrollTop(root, target);
        if (scrollTop == null) return;

        transcriptChunkStore.markProgrammaticScroll(smooth ? 450 : 250);
        if (smooth) {
          root.scrollTo({ top: scrollTop, behavior: 'smooth' });
        } else {
          root.scrollTop = scrollTop;
        }

        chunkSeekPerf('snap-scroll', {
          chunkId: target.chunkId,
          time: Number(target.time.toFixed(3)),
          smooth,
          scrollTop: Number(scrollTop.toFixed(1)),
          window: transcriptChunkStore.getActiveWindow(),
        });
        syncSeekViewportState(root);
      };

      const snapPlaybackSeek = () => {
        const inWindowGen = transcriptChunkStore.getInWindowSeekGeneration();
        if (inWindowGen !== lastInWindowSeekGenRef.current) {
          lastInWindowSeekGenRef.current = inWindowGen;
          ignoreScrollCatchUpRef.current = true;
          suppressViewportCatchUpRef.current = true;
          playbackSeekLoadingRef.current = false;
          seekIdleCleanupAppliedRef.current = true;
          setPlaybackSeekLoading(false);
          setScrollLoading(false);
          clearBoundaryScrollLoadState();
          postSeekGraceUntilRef.current = performance.now() + 400;
          if (inWindowSeekCatchUpTimerRef.current) {
            window.clearTimeout(inWindowSeekCatchUpTimerRef.current);
          }
          inWindowSeekCatchUpTimerRef.current = window.setTimeout(() => {
            suppressViewportCatchUpRef.current = false;
            ignoreScrollCatchUpRef.current = false;
            inWindowSeekCatchUpTimerRef.current = 0;
          }, CHUNK_DWCM_SCROLL_COOLDOWN_MS + 1200);
        }

        if (userScrollOverridesSeekRef.current) return;
        if (isLinePlaySeekGuardActive()) {
          transcriptChunkStore.cancelPlaybackSeek('line-play-guard');
          setPlaybackSeekLoading(false);
          return;
        }

        const target = transcriptChunkStore.getPlaybackSeekTarget();
        if (!target) {
          lastPlaybackSeekSnapRef.current = null;
          lastSeekSettledRef.current = null;
          if (transcriptChunkStore.isPlaybackSeekSliding()) {
            return;
          }
          pendingSeekScrollRef.current = null;
          playbackSeekLoadingRef.current = false;
          if (seekIdleCleanupAppliedRef.current) {
            return;
          }
          seekIdleCleanupAppliedRef.current = true;
          setPlaybackSeekLoading(false);
          postSeekGraceUntilRef.current = performance.now() + 400;
          clearBoundaryScrollLoadState();
          setScrollLoading(false);
          setViewportOutsideWindow(false);
          setViewportSpacers({ top: false, bottom: false });
          return;
        }
        seekIdleCleanupAppliedRef.current = false;
        ignoreScrollCatchUpRef.current = true;
        const sliding = transcriptChunkStore.isPlaybackSeekSliding();
        const snapKey = `${target.chunkId}:${target.time.toFixed(3)}`;
        const isNewTarget = lastPlaybackSeekSnapRef.current !== snapKey;

        const root = scrollRoot ?? resolveScrollRoot();
        if (!root) {
          if (seekRootRetryRef.current < 24) {
            seekRootRetryRef.current += 1;
            requestAnimationFrame(() => snapPlaybackSeek());
          } else {
            seekRootRetryRef.current = 0;
            chunkPerfLog('seek:no-scroll-root', { target });
            transcriptChunkStore.cancelPlaybackSeek('no-scroll-root');
            setPlaybackSeekLoading(false);
            playbackSeekLoadingRef.current = false;
            pendingSeekScrollRef.current = null;
            clearBoundaryScrollLoadState();
          }
          return;
        }
        seekRootRetryRef.current = 0;

        if (sliding) {
          // Drag: scroll preview only — no Slate remount until release (avoids lag).
          if (isNewTarget) {
            lastPlaybackSeekSnapRef.current = snapKey;
          }
          snapToTarget(root, target, false);
          return;
        }

        if (isNewTarget) {
          seekIdleCleanupAppliedRef.current = false;
          playbackSeekLoadingRef.current = true;
          setPlaybackSeekLoading(true);
          setViewportPx(root.clientHeight);
          lastPlaybackSeekSnapRef.current = snapKey;
        }

        if (lastSeekSettledRef.current !== snapKey) {
          lastSeekSettledRef.current = snapKey;
          settlePlaybackSeekWindow(root, target);
          snapToTarget(root, target, false);
          requestAnimationFrame(() => {
            if (
              !transcriptChunkStore.getPlaybackSeekTarget() ||
              userScrollOverridesSeekRef.current
            ) {
              return;
            }
            snapToTarget(root, target, false);
            tryFinishSeekIfEditorReady();
          });
          return;
        }
        tryFinishSeekIfEditorReady();
      };

      snapPlaybackSeek();

      const unsub = transcriptChunkStore.subscribe(snapPlaybackSeek);
      return () => {
        unsub();
        if (inWindowSeekCatchUpTimerRef.current) {
          window.clearTimeout(inWindowSeekCatchUpTimerRef.current);
          inWindowSeekCatchUpTimerRef.current = 0;
        }
      };
    }, [snapshot.index.length, heightFor, applyWindowChange, readSpacersForViewport, clearBoundaryScrollLoadState, refreshEditorDocument]);

    useEffect(() => {
      return transcriptChunkStore.subscribe(() => {
        if (initInFlightRef.current) return;
        if (transcriptChunkStore.isSaveInFlight()) return;

        const snap = transcriptChunkStore.getSnapshot();
        const w = snap.activeWindow;
        const key = w ? `${w.startOrdinal}-${w.endOrdinal}` : '';
        if (
          key &&
          lastWindowKeyRef.current &&
          key !== lastWindowKeyRef.current
        ) {
          applyWindowChange({});
        } else {
          const docRev = transcriptChunkStore.getDocumentRevision();
          if (docRev !== lastDocRevisionRef.current) {
            lastDocRevisionRef.current = docRev;
            // Same window key: avoid seek remount storms. Find-jump repair still
            // needs a Slate remount so waitUntilEditorReady can complete.
            refreshEditorDocument({
              remount: transcriptChunkStore.isFindJumpInProgress(),
            });
          }
        }
        setSnapshot((prev) => {
          if (
            prev.activeWindow?.startOrdinal === snap.activeWindow?.startOrdinal &&
            prev.activeWindow?.endOrdinal === snap.activeWindow?.endOrdinal &&
            prev.loadingChunkId === snap.loadingChunkId &&
            prev.editorReady === snap.editorReady &&
            prev.activeChunkId === snap.activeChunkId &&
            prev.index === snap.index &&
            prev.playbackSeekInProgress === snap.playbackSeekInProgress &&
            prev.transcriptWaitLocked === snap.transcriptWaitLocked &&
            prev.scrollLoading === snap.scrollLoading &&
            prev.inWindowSeekGeneration === snap.inWindowSeekGeneration
          ) {
            return prev;
          }
          return snap;
        });
      });
    }, [applyWindowChange, refreshEditorDocument]);

    useEffect(() => {
      const w = snapshot.activeWindow;
      const key = w ? `${w.startOrdinal}-${w.endOrdinal}` : '';
      if (key) setScrollRoot(resolveScrollRoot());
    }, [
      snapshot.activeWindow?.startOrdinal,
      snapshot.activeWindow?.endOrdinal,
    ]);

    useLayoutEffect(() => {
      const root = resolveScrollRoot();
      if (root) setScrollRoot(root);
    }, [
      scrollLoading,
      stressScrollPending,
      scrollFrozen,
      snapshot.editorReady,
      layoutTick,
    ]);

    /** Stress scroll: defer window catch-up until scroll settles — do not hard-lock mid-scroll. */
    useEffect(() => {
      if (snapshot.index.length <= 1) return;

      const root = scrollRoot ?? resolveScrollRoot();
      if (!root) return;

      const enterStressScroll = () => {
        if (scrollFrozenRef.current) return;
        scrollFrozenRef.current = true;
        transcriptChunkStore.setScrolling(true);
        setScrollFrozen(true);
        setStressScrollPending(true);
        setViewportPx(root.clientHeight);

        const video = window.Video as HTMLMediaElement | undefined;
        if (video && !video.paused && !video.ended) {
          const idx = indexRef.current;
          const centerOrd = viewportCenterOrdinal(
            root.scrollTop,
            root.clientHeight,
            idx,
            heightFor,
          );
          const centerMeta = idx[centerOrd];
          if (centerMeta) {
            transcriptChunkStore.markPlaybackPausedForLoad(centerMeta.chunkId);
            try {
              video.pause();
            } catch {
              // ignore
            }
          }
        }

        syncViewportOutside();
        chunkPerfLog('dwcm:stress-scroll', {
          scrollTop: Number(root.scrollTop.toFixed(1)),
          window: transcriptChunkStore.getActiveWindow(),
        });
      };

      const scheduleViewportSync = () => {
        if (!scrollFrozenRef.current) return;
        if (scrollViewportRafRef.current) return;
        scrollViewportRafRef.current = window.requestAnimationFrame(() => {
          scrollViewportRafRef.current = 0;
          syncViewportOutside();
        });
      };

      const syncViewportOutside = () => {
        const w = transcriptChunkStore.getActiveWindow();
        const idx = indexRef.current;
        if (!w || idx.length <= 1) {
          setViewportOutsideWindow(false);
          setViewportSpacers({ top: false, bottom: false });
          return;
        }
        if (transcriptChunkStore.isProgrammaticScroll()) {
          if (
            !transcriptChunkStore.getPlaybackSeekTarget() &&
            !transcriptChunkStore.isPlaybackSeekSliding()
          ) {
            return;
          }
        }
        const scrollTop = root.scrollTop;
        const clientHeight = root.clientHeight;
        const outside = viewportOutsideForScroll(
          idx,
          w,
          scrollTop,
          clientHeight,
        );
        const spacers = readSpacersForViewport(w, idx, scrollTop, clientHeight);
        if (outside || spacers.top || spacers.bottom) {
          setViewportPx(clientHeight);
        }
        setScrollMetrics({ scrollTop, clientHeight });
        setViewportSpacers(spacers);
        setViewportOutsideWindow(outside);
      };

      const scheduleCatchUpRetry = (force: boolean) => {
        if (transcriptChunkStore.isPlaybackSeekInProgress()) return;
        if (transcriptChunkStore.isFindJumpInProgress()) return;
        if (!transcriptChunkStore.isEditorReady()) return;
        if (catchUpInFlightRef.current && !force) return;
        if (catchUpRetryTimerRef.current) {
          window.clearTimeout(catchUpRetryTimerRef.current);
        }
        catchUpRetryTimerRef.current = window.setTimeout(() => {
          catchUpRetryTimerRef.current = 0;
          if (!transcriptChunkStore.isEditorReady()) return;
          catchUpInFlightRef.current = true;
          const caught = catchUpWindowForViewport({
            scrollRoot: root,
            index: indexRef.current,
            heightFor,
            onWindowChange: applyWindowChange,
            source: 'settle',
            force,
            scrollTopOverride: settleScrollTopOverride(),
          });
          catchUpInFlightRef.current = false;
          syncViewportOutside();
          if (caught && !transcriptChunkStore.isEditorReady()) {
            setLayoutTick((v) => v + 1);
            setScrollLoading(true);
          }
        }, CHUNK_DWCM_SCROLL_COOLDOWN_MS + 20);
      };

      const readViewportState = () => {
        const w = transcriptChunkStore.getActiveWindow();
        const idx = indexRef.current;
        if (!w || idx.length <= 1) return null;
        const scrollTop = root.scrollTop;
        const clientHeight = root.clientHeight;
        const outside = viewportOutsideForScroll(
          idx,
          w,
          scrollTop,
          clientHeight,
        );
        const spacers = readSpacersForViewport(w, idx, scrollTop, clientHeight);
        const target = resolveWindowForViewport(
          idx,
          scrollTop,
          clientHeight,
          heightFor,
        );
        const suppressPostSeekCatchUp =
          transcriptChunkStore.shouldSuppressPostSeekViewportCatchUp();
        const needsCatchUp =
          !suppressPostSeekCatchUp &&
          !transcriptChunkStore.isPlaybackSeekInProgress() &&
          !transcriptChunkStore.isFindJumpInProgress() &&
          (outside ||
            spacers.top ||
            spacers.bottom ||
            !windowsEqual(w, target));
        return {
          outside,
          spacers,
          needsCatchUp,
          expandingDown: target.endOrdinal > w.endOrdinal,
          expandingUp: target.startOrdinal < w.startOrdinal,
        };
      };

      const isTranscriptOverlayTarget = (target: EventTarget | null) =>
        !!(target as Element | null)?.closest?.(
          '[data-transcript-viewport-overlay="1"]',
        );

      const onDocumentWheelDuringLoad = (event: WheelEvent) => {
        if (!shouldBlockWheelDuringWindowLoad()) return;
        if (isTranscriptOverlayTarget(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        const scrollRootEl = scrollRoot ?? resolveScrollRoot();
        if (!scrollRootEl?.contains(event.target as Node)) return;
        syncTranscriptWheelLockCursor(scrollRootEl, true);
        event.preventDefault();
        event.stopImmediatePropagation();
      };

      const onDocumentTouchMoveDuringLoad = (event: TouchEvent) => {
        if (!shouldBlockWheelDuringWindowLoad()) return;
        if (isTranscriptOverlayTarget(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        const scrollRootEl = scrollRoot ?? resolveScrollRoot();
        if (!scrollRootEl?.contains(event.target as Node)) return;
        syncTranscriptWheelLockCursor(scrollRootEl, true);
        event.preventDefault();
        event.stopImmediatePropagation();
      };

      document.addEventListener('wheel', onDocumentWheelDuringLoad, {
        passive: false,
        capture: true,
      });
      document.addEventListener('touchmove', onDocumentTouchMoveDuringLoad, {
        passive: false,
        capture: true,
      });

      const deferViewportCatchUpWhileLoading = () => {
        if (transcriptChunkStore.isFindJumpInProgress()) {
          syncViewportOutside();
          return;
        }
        syncViewportOutside();
        catchUpWindowForViewport({
          scrollRoot: root,
          index: indexRef.current,
          heightFor,
          onWindowChange: applyWindowChange,
          source: 'settle',
          force: true,
          scrollTopOverride: settleScrollTopOverride(),
        });
      };

      const runForcedCatchUp = () => {
        if (suppressViewportCatchUpRef.current) return false;
        if (transcriptChunkStore.shouldSuppressPostSeekViewportCatchUp()) {
          return false;
        }
        if (transcriptChunkStore.isPlaybackSeekInProgress()) return false;
        if (transcriptChunkStore.isFindJumpInProgress()) return false;
        if (catchUpInFlightRef.current) return false;
        if (!transcriptChunkStore.isEditorReady()) {
          deferViewportCatchUpWhileLoading();
          return false;
        }
        if (!transcriptChunkStore.isEditorReady()) {
          setScrollLoading(true);
        }
        catchUpInFlightRef.current = true;
        const caught = catchUpWindowForViewport({
          scrollRoot: root,
          index: indexRef.current,
          heightFor,
          onWindowChange: applyWindowChange,
          source: 'settle',
          force: true,
          scrollTopOverride: settleScrollTopOverride(),
        });
        catchUpInFlightRef.current = false;
        syncViewportOutside();
        setLayoutTick((v) => v + 1);
        const w = transcriptChunkStore.getActiveWindow();
        const idx = indexRef.current;
        if (
          w &&
          idx.length > 1 &&
          viewportNeedsWindowCatchUp(
            idx,
            w,
            root.scrollTop,
            root.clientHeight,
            heightFor,
          )
        ) {
          scheduleCatchUpRetry(true);
        }
        return caught;
      };

      const onScrollSettle = () => {
        userScrollOverridesSeekRef.current = false;

        if (transcriptChunkStore.isPlaybackSeekInProgress()) {
          syncViewportOutside();
          return;
        }
        if (transcriptChunkStore.isFindJumpInProgress()) {
          syncViewportOutside();
          return;
        }

        measureIdleTimerRef.current = 0;
        scrollIdleRef.current = true;

        const wasFrozen = scrollFrozenRef.current;
        const editorReady = transcriptChunkStore.isEditorReady();

        if (scrollLoadLockRef.current && editorReady) {
          syncViewportOutside();
          return;
        }

        /** One final anchor per settle — not on every scroll/wheel event. */
        if (editorReady) {
          recordFinalScrollOnSettle(root);
        }

        if (ignoreScrollCatchUpRef.current && !wasFrozen && editorReady) {
          syncViewportOutside();
          const vp = readViewportState();
          if (vp?.needsCatchUp && !suppressViewportCatchUpRef.current) {
            runForcedCatchUp();
          }
          return;
        }

        let pinnedChunkId: string | null = null;
        if (wasFrozen) {
          const idx = indexRef.current;
          const centerOrd = viewportCenterOrdinal(
            root.scrollTop,
            root.clientHeight,
            idx,
            heightFor,
          );
          const centerMeta = idx[centerOrd];
          if (centerMeta) {
            pinnedChunkId = centerMeta.chunkId;
            transcriptChunkStore.setScrollPinnedChunk(centerMeta.chunkId);
            const video = window.Video as HTMLMediaElement | undefined;
            if (video && !video.paused && !video.ended) {
              const t = video.currentTime || 0;
              if (!transcriptChunkStore.isTimeInChunk(centerMeta.chunkId, t)) {
                transcriptChunkStore.markPlaybackPausedForLoad(
                  centerMeta.chunkId,
                );
                try {
                  video.pause();
                } catch {
                  // ignore
                }
              }
            }
          }
        }

        if (wasFrozen) {
          scrollFrozenRef.current = false;
          transcriptChunkStore.setScrolling(false);
          setScrollFrozen(false);
          if (!editorReady) {
            setScrollLoading(true);
          }
          ignoreScrollCatchUpRef.current = true;
        }

        if (!editorReady) {
          if (!editorEverReadyRef.current) {
            syncViewportOutside();
            return;
          }
          if (transcriptChunkStore.isPlaybackSeekInProgress()) {
            syncViewportOutside();
            return;
          }
          if (!scrollLoadLockRef.current) {
            armWheelBlockAtTrigger(root);
          }
          deferViewportCatchUpWhileLoading();
          if (wasFrozen) {
            chunkPerfLog('dwcm:settle', {
              scrollTop: Number(root.scrollTop.toFixed(1)),
              caught: false,
              stillNeedsWindow: true,
              window: transcriptChunkStore.getActiveWindow(),
              pinned: pinnedChunkId,
              deferred: true,
            });
          }
          return;
        }

        const vpSettle = readViewportState();
        if (suppressViewportCatchUpRef.current) {
          syncViewportOutside();
          return;
        }
        if (vpSettle?.needsCatchUp) {
          if (!scrollLoadLockRef.current) {
            armWheelBlockAtTrigger(root);
            setScrollLoading(true);
          }
        }

        if (
          vpSettle &&
          !ignoreScrollCatchUpRef.current &&
          viewportNeedsStressScroll(vpSettle)
        ) {
          enterStressScroll();
          scheduleViewportSync();
        } else if (
          vpSettle &&
          ignoreScrollCatchUpRef.current &&
          viewportNeedsStressScroll(vpSettle)
        ) {
          ignoreScrollCatchUpRef.current = false;
          enterStressScroll();
          scheduleViewportSync();
        }

        if (catchUpInFlightRef.current) {
          // S5: still record the latest viewport target while a remount is in flight.
          if (!transcriptChunkStore.isEditorReady()) {
            deferViewportCatchUpWhileLoading();
            setScrollLoading(true);
          }
          scheduleCatchUpRetry(wasFrozen || !!vpSettle?.needsCatchUp);
          syncViewportOutside();
          return;
        }

        catchUpInFlightRef.current = true;
        const caught = catchUpWindowForViewport({
          scrollRoot: root,
          index: indexRef.current,
          heightFor,
          onWindowChange: applyWindowChange,
          source: 'settle',
          force: wasFrozen || !!vpSettle?.needsCatchUp,
          scrollTopOverride: settleScrollTopOverride(),
        });
        catchUpInFlightRef.current = false;

        syncViewportOutside();
        setLayoutTick((v) => v + 1);

        const w = transcriptChunkStore.getActiveWindow();
        const idx = indexRef.current;
        const stillNeedsWindow =
          !!w &&
          idx.length > 1 &&
          viewportNeedsWindowCatchUp(
            idx,
            w,
            root.scrollTop,
            root.clientHeight,
            heightFor,
          );

        if (stillNeedsWindow) {
          scheduleCatchUpRetry(true);
        } else if (wasFrozen) {
          if (!caught && !pendingSettleScrollRef.current) {
            refreshEditorDocument();
          }
          if (
            transcriptChunkStore.isEditorReady() &&
            !pendingSettleScrollRef.current
          ) {
            const outside = viewportOutsideForScroll(
              idx,
              w!,
              root.scrollTop,
              root.clientHeight,
            );
            const band = getContentBand();
            const spacers = band
              ? viewportSpacerIntersectionForBand(
                  root.scrollTop,
                  root.clientHeight,
                  band,
                )
              : viewportSpacerIntersection(
                  idx,
                  w!,
                  root.scrollTop,
                  root.clientHeight,
                  heightFor,
                );
            if (!outside && !spacers.top && !spacers.bottom) {
              setViewportOutsideWindow(false);
              setViewportSpacers({ top: false, bottom: false });
              setStressScrollPending(false);
            }
          }
        }

        if (wasFrozen) {
          chunkPerfLog('dwcm:settle', {
            scrollTop: Number(root.scrollTop.toFixed(1)),
            caught,
            stillNeedsWindow,
            window: transcriptChunkStore.getActiveWindow(),
            pinned: pinnedChunkId,
            restorePending: !!pendingSettleScrollRef.current,
          });
        }

        if (
          pendingSettleScrollRef.current &&
          transcriptChunkStore.isEditorReady() &&
          !stillNeedsWindow
        ) {
          requestAnimationFrame(() => finishSettleScrollPresentation());
        }
      };

      /** One-chunk upward slide — load full previous chunk without stress-scroll freeze. */
      const isFastUpwardExpand = (vp: ReturnType<typeof readViewportState>) => {
        if (!vp?.expandingUp || !vp.needsCatchUp) return false;
        const w = transcriptChunkStore.getActiveWindow();
        if (!w) return false;
        const target = resolveWindowForViewport(
          indexRef.current,
          root.scrollTop,
          root.clientHeight,
          heightFor,
        );
        return isSingleChunkUpwardSlide(w, target);
      };

      const viewportNeedsStressScroll = (
        vp: ReturnType<typeof readViewportState>,
      ) => {
        if (!vp) return false;
        if (isFastUpwardExpand(vp)) return false;
        if (vp.outside) return true;
        if (vp.spacers.top && vp.expandingUp) return true;
        if (vp.spacers.bottom && vp.expandingDown) return true;
        return false;
      };

      const scrollSettleMs = () =>
        scrollFrozenRef.current
          ? CHUNK_SCROLL_SETTLE_MS
          : CHUNK_DWCM_CATCHUP_MS;

      const onScroll = () => {
        if (!transcriptChunkStore.isProgrammaticScroll()) {
          if (transcriptChunkStore.isPlaybackSeekInProgress()) {
            userScrollOverridesSeekRef.current = true;
            cancelActivePlaybackSeek('user-scroll');
          }
        }

        if (transcriptChunkStore.isProgrammaticScroll()) {
          if (
            transcriptChunkStore.getPlaybackSeekTarget() ||
            transcriptChunkStore.isPlaybackSeekSliding()
          ) {
            syncViewportOutside();
          }
          return;
        }

        scrollIdleRef.current = false;

        if (scrollLoadLockRef.current) {
          if (loadScrollAnchorRef.current != null) {
            const anchor = loadScrollAnchorRef.current;
            if (Math.abs(root.scrollTop - anchor) > 2) {
              transcriptChunkStore.markProgrammaticScroll(250);
              root.scrollTop = anchor;
            }
          }
          if (measureIdleTimerRef.current) {
            window.clearTimeout(measureIdleTimerRef.current);
            measureIdleTimerRef.current = 0;
          }
          if (catchUpRetryTimerRef.current) {
            window.clearTimeout(catchUpRetryTimerRef.current);
            catchUpRetryTimerRef.current = 0;
          }
          return;
        }

        if (!scrollViewportRafRef.current) {
          scrollViewportRafRef.current = window.requestAnimationFrame(() => {
            scrollViewportRafRef.current = 0;
            liveScrollDraftRef.current = {
              scrollTop: root.scrollTop,
              clientHeight: root.clientHeight,
            };
            setScrollMetrics({
              scrollTop: root.scrollTop,
              clientHeight: root.clientHeight,
            });
            syncViewportOutside();
            if (!transcriptChunkStore.isEditorReady()) {
              setLayoutTick((v) => v + 1);
            }
          });
        }

        if (measureIdleTimerRef.current) {
          window.clearTimeout(measureIdleTimerRef.current);
        }
        measureIdleTimerRef.current = window.setTimeout(
          onScrollSettle,
          scrollSettleMs(),
        );
      };

      const onWheelDuringWindowLoad = (event: WheelEvent) => {
        if (!shouldBlockWheelDuringWindowLoad()) return;
        syncTranscriptWheelLockCursor(root, true);
        event.preventDefault();
        event.stopPropagation();
      };

      const onTouchMoveDuringWindowLoad = (event: TouchEvent) => {
        if (!shouldBlockWheelDuringWindowLoad()) return;
        syncTranscriptWheelLockCursor(root, true);
        event.preventDefault();
        event.stopPropagation();
      };

      const onKeyScrollDuringLoad = (event: KeyboardEvent) => {
        if (!shouldBlockWheelDuringWindowLoad()) return;
        const target = event.target as Node | null;
        if (target && !root.contains(target) && target !== root) return;
        const scrollKeys = new Set([
          'PageUp',
          'PageDown',
          'Home',
          'End',
          'ArrowUp',
          'ArrowDown',
          ' ',
        ]);
        if (!scrollKeys.has(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
      };

      root.addEventListener('scroll', onScroll, { passive: true });
      root.addEventListener('wheel', onWheelDuringWindowLoad, {
        passive: false,
        capture: true,
      });
      root.addEventListener('touchmove', onTouchMoveDuringWindowLoad, {
        passive: false,
        capture: true,
      });

      const onUserScrollIntent = (event: Event) => {
        if (scrollLoadLockRef.current) return;
        if (transcriptChunkStore.isPlaybackSeekInProgress()) {
          userScrollOverridesSeekRef.current = true;
          cancelActivePlaybackSeek('user-scroll-intent');
        }
        /** Navigation + anchor are recorded once in onScrollSettle, not per wheel/key. */
      };
      const onScrollbarPointerDown = (event: MouseEvent) => {
        if (event.button !== 0) return;
        if (!isTranscriptScrollbarPointer(root, event.clientX)) return;
        if (shouldHardLockScroll()) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        scrollbarDragActiveRef.current = true;
      };
      const onScrollbarPointerUp = () => {
        scrollbarDragActiveRef.current = false;
      };
      root.addEventListener('keydown', onKeyScrollDuringLoad, true);
      root.addEventListener('wheel', onUserScrollIntent, { passive: true });
      root.addEventListener('touchstart', onUserScrollIntent, {
        passive: true,
      });
      root.addEventListener('keydown', onUserScrollIntent);
      root.addEventListener('mousedown', onScrollbarPointerDown);
      document.addEventListener('mouseup', onScrollbarPointerUp);

      return () => {
        document.removeEventListener('mouseup', onScrollbarPointerUp);
        document.removeEventListener('wheel', onDocumentWheelDuringLoad, true);
        document.removeEventListener(
          'touchmove',
          onDocumentTouchMoveDuringLoad,
          true,
        );
        root.removeEventListener('scroll', onScroll);
        root.removeEventListener('wheel', onWheelDuringWindowLoad, true);
        root.removeEventListener(
          'touchmove',
          onTouchMoveDuringWindowLoad,
          true,
        );
        root.removeEventListener('wheel', onUserScrollIntent);
        root.removeEventListener('touchstart', onUserScrollIntent);
        root.removeEventListener('keydown', onUserScrollIntent);
        root.removeEventListener('keydown', onKeyScrollDuringLoad, true);
        root.removeEventListener('mousedown', onScrollbarPointerDown);
        if (postLoadCooldownTimerRef.current) {
          window.clearTimeout(postLoadCooldownTimerRef.current);
          postLoadCooldownTimerRef.current = 0;
        }
        if (scrollPaneLockedRef.current) {
          syncTranscriptWheelLockCursor(root, false);
          scrollPaneLockedRef.current = false;
        }
        if (measureIdleTimerRef.current) {
          window.clearTimeout(measureIdleTimerRef.current);
        }
        if (scrollViewportRafRef.current) {
          window.cancelAnimationFrame(scrollViewportRafRef.current);
          scrollViewportRafRef.current = 0;
        }
        if (catchUpRetryTimerRef.current) {
          window.clearTimeout(catchUpRetryTimerRef.current);
        }
        transcriptChunkStore.setScrolling(false);
      };
    }, [
      scrollRoot,
      heightFor,
      applyWindowChange,
      snapshot.index.length,
      cancelActivePlaybackSeek,
      getContentBand,
      readSpacersForViewport,
      viewportOutsideForScroll,
      shouldBlockWheelDuringWindowLoad,
      shouldHardLockScroll,
      syncWheelLoadBlock,
      captureSettleScrollAnchor,
      recordFinalScrollOnSettle,
      armWheelBlockAtTrigger,
    ]);

    useEffect(() => {
      if (!snapshot.editorReady || !ignoreScrollCatchUpRef.current) return;
      const timer = window.setTimeout(() => {
        ignoreScrollCatchUpRef.current = false;
      }, CHUNK_DWCM_SCROLL_COOLDOWN_MS + 1200);
      return () => window.clearTimeout(timer);
    }, [snapshot.editorReady, snapshot.activeWindow?.startOrdinal, snapshot.activeWindow?.endOrdinal]);

    useEffect(() => {
      if (isEmpty(fullDocument)) {
        transcriptChunkStore.clear();
        slotHeightRef.current = {};
        rowPxRef.current = CHUNK_PLACEHOLDER_ROW_PX;
        lastWindowKeyRef.current = '';
        editorEverReadyRef.current = false;
        setEditorDocument([]);
        setScrollLoading(false);
        return;
      }
      if (transcriptChunkStore.matchesDocument(fullDocument, documentKey)) {
        chunkPerfLog('dwcm-shell:init-skip-unchanged-document', {
          activeWindow: transcriptChunkStore.getActiveWindow(),
        });
        const snap = transcriptChunkStore.getSnapshot();
        setSnapshot(snap);
        const w = snap.activeWindow;
        if (w) {
          lastWindowKeyRef.current = `${w.startOrdinal}-${w.endOrdinal}`;
        }
        lastDocRevisionRef.current = transcriptChunkStore.getDocumentRevision();
        setAllowEditorMount(true);
        return;
      }

      if (transcriptChunkStore.isSaveInFlight()) {
        chunkPerfLog('dwcm-shell:init-skip-save-in-flight');
        return;
      }

      initInFlightRef.current = true;
      lastWindowKeyRef.current = '';
      lastDocRevisionRef.current = -1;
      editorEverReadyRef.current = false;
      try {
        transcriptChunkStore.initFromFullDocument(
          fullDocument as TranscriptBlock[],
          undefined,
          documentKey,
        );
        const snap = transcriptChunkStore.getSnapshot();
        slotHeightRef.current = {};
        rowPxRef.current = CHUNK_PLACEHOLDER_ROW_PX;
        const boot = snap.activeWindow;
        const merged = transcriptChunkStore.getMergedWindowNodes();
        const w = transcriptChunkStore.getActiveWindow();
        if (w) {
          lastWindowKeyRef.current = `${w.startOrdinal}-${w.endOrdinal}`;
        }
        lastDocRevisionRef.current = transcriptChunkStore.getDocumentRevision();
        chunkPerfLog('dwcm-shell:init-boot', {
          window: w,
          blockCount: merged.length,
          chunkOrdinals: boot
            ? boot.endOrdinal - boot.startOrdinal + 1
            : 0,
        });
        setSnapshot(snap);
        setEditorDocument(
          merged.length ? cloneNodesForSlateEditor(merged) : [],
        );
        setAllowEditorMount(true);
        setScrollLoading(!snap.editorReady);
        const root = resolveScrollRoot();
        const anchorTime = transcriptChunkStore.consumeModeSwitchAnchorTime();
        const anchorResolved =
          anchorTime != null && snap.index.length > 0
            ? transcriptChunkStore.resolveChunkForTime(anchorTime, {
                playing: false,
              })
            : null;
        if (anchorResolved?.meta && snap.index.length > 1) {
          const targetWindow = resolveWindowAroundOrdinal(
            anchorResolved.meta.ordinal,
            snap.index.length,
          );
          transcriptChunkStore.setActiveWindowDirect(targetWindow, 'mode-switch');
          const anchoredSnap = transcriptChunkStore.getSnapshot();
          const anchoredMerged = transcriptChunkStore.getMergedWindowNodes();
          setSnapshot(anchoredSnap);
          setEditorDocument(
            anchoredMerged.length
              ? cloneNodesForSlateEditor(anchoredMerged)
              : [],
          );
          setScrollLoading(!anchoredSnap.editorReady);
          pendingSeekScrollRef.current = {
            chunkId: anchorResolved.meta.chunkId,
            time: anchorTime!,
          };
          if (root) {
            setViewportPx(root.clientHeight);
            setScrollMetrics({
              scrollTop: root.scrollTop,
              clientHeight: root.clientHeight,
            });
          }
        } else if (root) {
          setViewportPx(root.clientHeight);
          transcriptChunkStore.markProgrammaticScroll(250);
          root.scrollTop = 0;
          loadScrollAnchorRef.current = 0;
          setScrollMetrics({
            scrollTop: 0,
            clientHeight: root.clientHeight,
          });
        }
        ignoreScrollCatchUpRef.current = false;
      } finally {
        initInFlightRef.current = false;
      }
    }, [fullDocument, documentKey]);

    useEffect(() => {
      return () => {
        if (measureIdleTimerRef.current) {
          window.clearTimeout(measureIdleTimerRef.current);
        }
        if (catchUpRetryTimerRef.current) {
          window.clearTimeout(catchUpRetryTimerRef.current);
        }
      };
    }, []);

    const isEditMode = useSelector((state: RootState) => state.toggle.isEditMode);

    useLayoutEffect(() => {
      const w = snapshot.activeWindow;
      if (!w || !editorDocument.length) return;
      if (!transcriptChunkStore.isEditorReady()) return;
      // Add/split/merge grows the live editor. Recalibrating far-chunk spacers
      // from that measure jumps scrollTop away from the edited row.
      if (isEditMode) return;

      const editorEl = editorWindowRef.current?.querySelector(
        '[data-slate-editor]',
      ) as HTMLElement | null;
      const measured = Math.round(editorEl?.offsetHeight ?? 0);
      if (measured < CHUNK_MIN_MEASURED_PX) return;

      const idx = indexRef.current;
      const slotHeightAt = (meta: TranscriptChunkMeta) =>
        slotHeightRef.current[meta.chunkId] ??
        estimateChunkHeight(meta.blockCount, rowPxRef.current);
      const beforeTop = spacerHeightBefore(idx, w.startOrdinal, slotHeightAt);

      const { nextSlots, rowPx, changed } = calibrateSlotHeightsFromWindowMeasure(
        idx,
        w,
        measured,
        slotHeightRef.current,
      );
      if (!changed) return;

      slotHeightRef.current = nextSlots;
      rowPxRef.current = rowPx;

      const afterTop = spacerHeightBefore(idx, w.startOrdinal, slotHeightAt);
      const root = scrollRoot ?? resolveScrollRoot();
      if (root && beforeTop !== afterTop) {
        transcriptChunkStore.markProgrammaticScroll(CHUNK_DWCM_SCROLL_COOLDOWN_MS);
        root.scrollTop = Math.max(0, root.scrollTop + (afterTop - beforeTop));
      }

      chunkPerfLog('dwcm:measure-window', {
        measuredPx: measured,
        rowPx: Number(rowPx.toFixed(1)),
        window: w,
        topDelta: afterTop - beforeTop,
      });
      setLayoutTick((v) => v + 1);
    }, [
      editorDocument.length,
      snapshot.activeWindow,
      snapshot.index,
      snapshot.editorReady,
      scrollRoot,
      isEditMode,
    ]);

    const activeWindow = snapshot.activeWindow;
    const topSpacer = useMemo(() => {
      if (!activeWindow) return 0;
      return spacerHeightBefore(
        snapshot.index,
        activeWindow.startOrdinal,
        heightFor,
      );
    }, [activeWindow, snapshot.index, heightFor, layoutTick]);

    const bottomSpacer = useMemo(() => {
      if (!activeWindow) return 0;
      return spacerHeightAfter(
        snapshot.index,
        activeWindow.endOrdinal,
        heightFor,
      );
    }, [activeWindow, snapshot.index, heightFor, layoutTick]);

    const handleSetDocument = (nodes: CustomElement[]) => {
      // View mode: never push Slate onChange back into the controlled value prop —
      // that reconciles against a fresh mount and duplicates React/Slate keys.
      if (isEditMode) {
        setEditorDocument(nodes);
        transcriptChunkStore.flushEditorWindowToStore(
          nodes as TranscriptBlock[],
          'dwcm-editor-change',
        );
      }
    };

    const editorWindowKey = activeWindow
      ? `${activeWindow.startOrdinal}-${activeWindow.endOrdinal}`
      : 'dwcm-0';
    const slateMountKey = `${editorWindowKey}:${editorDocGeneration}`;

    const playbackSeekTarget = transcriptChunkStore.getPlaybackSeekTarget();
    const editorReadyLive = transcriptChunkStore.isEditorReady();
    const inPostSeekGrace = performance.now() < postSeekGraceUntilRef.current;
    const seekLoadingUi = playbackSeekLoading && !editorReadyLive;
    const seekBusy = seekLoadingUi || !!playbackSeekTarget;

    /** Read scroll root live so spacer skeletons never lag one frame after settle. */
    const liveViewport = useMemo(() => {
      const root = scrollRoot ?? resolveScrollRoot();
      const w = activeWindow;
      const idx = snapshot.index;
      if (!root || !w || idx.length <= 1) {
        return {
          scrollTop: scrollMetrics.scrollTop,
          clientHeight: scrollMetrics.clientHeight || viewportPx,
          outside: viewportOutsideWindow,
          spacers: viewportSpacers,
        };
      }
      const scrollTop = root.scrollTop;
      const clientHeight = root.clientHeight || viewportPx;
      const target = resolveWindowForViewport(
        idx,
        scrollTop,
        clientHeight,
        heightFor,
      );
      const band = getContentBand();
      const spacers = band
        ? viewportSpacerIntersectionForBand(scrollTop, clientHeight, band)
        : viewportSpacerIntersection(
            idx,
            w,
            scrollTop,
            clientHeight,
            heightFor,
          );
      const outside = viewportOutsideForScroll(
        idx,
        w,
        scrollTop,
        clientHeight,
      );
      const needsCatchUp =
        !transcriptChunkStore.shouldSuppressPostSeekViewportCatchUp() &&
        !inPostSeekGrace &&
        !transcriptChunkStore.isPlaybackSeekInProgress() &&
        !seekLoadingUi &&
        (outside ||
          spacers.top ||
          spacers.bottom ||
          !windowsEqual(w, target));
      return {
        scrollTop,
        clientHeight,
        outside,
        spacers,
        needsCatchUp,
        expandingDown: target.endOrdinal > w.endOrdinal,
        expandingUp: target.startOrdinal < w.startOrdinal,
      };
    }, [
      scrollRoot,
      activeWindow,
      snapshot.index,
      layoutTick,
      heightFor,
      scrollMetrics.scrollTop,
      scrollMetrics.clientHeight,
      viewportPx,
      viewportOutsideWindow,
      viewportSpacers,
      seekLoadingUi,
      inPostSeekGrace,
      viewportOutsideForScroll,
    ]);

    const viewportAwaitingWindow =
      !seekBusy &&
      (liveViewport.outside ||
        liveViewport.spacers.top ||
        liveViewport.spacers.bottom);

    const viewportInLoadingZone =
      liveViewport.outside ||
      liveViewport.spacers.top ||
      liveViewport.spacers.bottom;

    const stressScrollBusy = scrollFrozen || stressScrollPending;

    const transitionBusy =
      (scrollLoading && !editorReadyLive) ||
      stressScrollBusy ||
      seekBusy;

    const windowLoading =
      !inPostSeekGrace &&
      (transitionBusy ||
        (viewportAwaitingWindow && !editorReadyLive));

    const chunkLoading = seekBusy || windowLoading;

    const seamPeekPx = CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX;

    const boundarySkeletonOverlay = useMemo(() => {
      const w = activeWindow;
      const idx = snapshot.index;
      if (!w || idx.length <= 1) {
        return {
          topPx: 0,
          bottomPx: 0,
          bottomOffsetTopPx: 0,
          triggers: { top: false, bottom: false },
        };
      }
      const scrollTop = liveViewport.scrollTop;
      const clientHeight = liveViewport.clientHeight;
      const triggers = windowBoundaryTriggers(
        idx,
        w,
        scrollTop,
        clientHeight,
        heightFor,
      );
      const editorEl = editorWindowRef.current?.querySelector(
        '[data-slate-editor]',
      ) as HTMLElement | null;
      const measuredEditorH =
        editorEl && editorEl.offsetHeight >= CHUNK_MIN_MEASURED_PX
          ? editorEl.offsetHeight
          : undefined;
      const overlay = resolveBoundarySkeletonOverlayPx(
        idx,
        w,
        scrollTop,
        clientHeight,
        heightFor,
        undefined,
        measuredEditorH,
      );
      return { ...overlay, triggers };
    }, [
      activeWindow,
      snapshot.index,
      liveViewport.scrollTop,
      liveViewport.clientHeight,
      heightFor,
      layoutTick,
    ]);

    const atBoundaryTrigger =
      boundarySkeletonOverlay.triggers.top ||
      boundarySkeletonOverlay.triggers.bottom;

    const viewportNeedsCatchUpForLock =
      !editorReadyLive &&
      !!liveViewport.needsCatchUp &&
      (atBoundaryTrigger ||
        liveViewport.outside ||
        liveViewport.spacers.top ||
        liveViewport.spacers.bottom);

    /** Wait cursor for in-flight load — not leftover scrollLoading after editor is ready. */
    const transcriptLoadingCueActive =
      !inPostSeekGrace &&
      (chunkLoading ||
        wheelBlockActive ||
        (!editorReadyLive && scrollLoading) ||
        viewportNeedsCatchUpForLock ||
        (atBoundaryTrigger &&
          !!liveViewport.needsCatchUp &&
          !editorReadyLive));

    const transcriptInteractionLocked = resolveTranscriptInteractionLocked({
      isEditMode,
      allowEditorMount,
      editorReady: editorReadyLive,
      chunkLoading: chunkLoading && !editorReadyLive,
      scrollFrozen,
      stressScrollPending,
      scrollLoading,
      viewportNeedsCatchUp: viewportNeedsCatchUpForLock,
      seekLoadingUi,
      hasPlaybackSeekTarget: !!playbackSeekTarget,
    });

    /** Domain wait lock — shared formula; store publishes for Player/resume (Track B). */
    const transcriptWaitLocked = resolveDwcmTranscriptWaitLocked({
      isEditMode,
      allowEditorMount,
      editorReady: editorReadyLive,
      chunkLoading: chunkLoading && !editorReadyLive,
      scrollFrozen,
      stressScrollPending,
      scrollLoading,
      viewportNeedsCatchUp: viewportNeedsCatchUpForLock,
      seekLoadingUi,
      hasPlaybackSeekTarget: !!playbackSeekTarget,
      wheelBlockActive,
      atBoundaryTrigger,
    });

    useEffect(() => {
      if (!transcriptChunkStore.isEnabled()) return;
      transcriptChunkStore.setUxPipelineGuards({
        scrollLoading,
        stressScrollPending,
        scrollFrozen,
        wheelBlockActive,
        atBoundaryTrigger,
        viewportNeedsCatchUp: viewportNeedsCatchUpForLock,
        seekLoadingUi,
        hasPlaybackSeekTarget: !!playbackSeekTarget,
        allowEditorMount,
        chunkLoading: chunkLoading && !editorReadyLive,
        isEditMode,
      });
    }, [
      scrollLoading,
      stressScrollPending,
      scrollFrozen,
      wheelBlockActive,
      atBoundaryTrigger,
      viewportNeedsCatchUpForLock,
      seekLoadingUi,
      playbackSeekTarget,
      allowEditorMount,
      chunkLoading,
      isEditMode,
    ]);

    /** Retry auto-resume once wait-lock / pipeline clear after Slate ready. */
    useEffect(() => {
      if (!snapshot.editorReady || transcriptWaitLocked) return;
      if (scrollLoading || stressScrollPending || seekBusy) return;
      tryResumePlaybackAfterChunkReady();
    }, [
      snapshot.editorReady,
      transcriptWaitLocked,
      scrollLoading,
      stressScrollPending,
      seekBusy,
    ]);

    /** Active chunk load at seam — suppress full-viewport skeleton overlays. */
    const boundaryChunkLoading =
      chunkLoading &&
      (liveViewport.expandingUp || liveViewport.expandingDown);

    const overlayHeight =
      viewportPx || CHUNK_PLACEHOLDER_ROW_PX * 12;

    const boundaryLoadingActive =
      !inPostSeekGrace &&
      (transcriptWaitLocked ||
        scrollLoading ||
        scrollFrozen ||
        stressScrollPending ||
        chunkLoading ||
        wheelBlockActive ||
        !!liveViewport.needsCatchUp);

    const showBoundarySkeletonCue =
      atBoundaryTrigger && boundaryLoadingActive;

    /** Hard-lock scroll for the full chunk-load pipeline (not leftover seam chrome). */
    const chunkScrollHardLocked =
      !inPostSeekGrace &&
      editorEverReadyRef.current &&
      wheelBlockActive &&
      !editorReadyLive;

    /** Viewport is in far-chunk spacer/outside band — catch-up pending while editor still mounted. */
    const farChunkViewportPending =
      !inPostSeekGrace &&
      editorEverReadyRef.current &&
      editorReadyLive &&
      viewportInLoadingZone &&
      !!liveViewport.needsCatchUp &&
      !wheelBlockActive;

    /** Scrollbar / wheel-trigger far-chunk load — wait cursor without overlay yet. */
    const farChunkScrollbarLoading =
      !inPostSeekGrace &&
      editorEverReadyRef.current &&
      viewportInLoadingZone &&
      !!liveViewport.needsCatchUp &&
      (wheelBlockActive || scrollLoading || !editorReadyLive);

    const loadingZoneInteractionBlocked =
      !inPostSeekGrace &&
      viewportInLoadingZone &&
      !editorReadyLive &&
      (scrollLoading || wheelBlockActive);

    /** Full-viewport overlay — S2/S3 remount + gap fill when locked with no localized chrome. */
    const showViewportProcessingOverlay = resolveShowViewportProcessingOverlay({
      inPostSeekGrace,
      editorEverReady: editorEverReadyRef.current,
      chunkScrollHardLocked,
      loadingZoneInteractionBlocked,
      stressScrollBusy,
      seekBusy,
      scrollLoading,
      editorReady: editorReadyLive,
      boundaryChunkLoading: boundaryChunkLoading ?? false,
      transcriptWaitLocked,
      hasBoundarySkeletonCue: showBoundarySkeletonCue,
      farChunkViewportPending:
        farChunkViewportPending || farChunkScrollbarLoading,
    });

    chunkScrollHardLockRef.current = chunkScrollHardLocked;
    transcriptScrollBlockedRef.current = showViewportProcessingOverlay;

    useLayoutEffect(() => {
      syncWheelLoadBlock();
    }, [
      showViewportProcessingOverlay,
      chunkScrollHardLocked,
      wheelBlockActive,
      scrollFrozen,
      scrollLoading,
      syncWheelLoadBlock,
    ]);

    const showLoadingChrome =
      showViewportProcessingOverlay ||
      farChunkViewportPending ||
      farChunkScrollbarLoading;

    /** Wait cursor while far-chunk load or remount is in flight. */
    const showWaitCursor = showLoadingChrome;

    /** Sticky viewport skeleton paired with wait cursor — never a blank loading pane. */
    const showFarChunkViewportSkeleton =
      showLoadingChrome &&
      viewportInLoadingZone &&
      !!liveViewport.needsCatchUp;

    useTranscriptChunkLoadingCue(
      showLoadingChrome ||
        resolveTranscriptLoadingCue(
          transcriptInteractionLocked,
          transcriptLoadingCueActive,
        ),
      { waitCursor: showWaitCursor },
    );

    const seamCueForEdge = (edge: 'top' | 'bottom') => {
      const atTrigger =
        edge === 'top'
          ? boundarySkeletonOverlay.triggers.top
          : boundarySkeletonOverlay.triggers.bottom;
      return atTrigger && boundaryLoadingActive;
    };

    const renderEditorSeamSkeleton = (edge: 'top' | 'bottom') => {
      if (showViewportProcessingOverlay) return null;
      const inSpacer =
        edge === 'top' ? liveViewport.spacers.top : liveViewport.spacers.bottom;
      const rawOverlayPx =
        edge === 'top'
          ? boundarySkeletonOverlay.topPx
          : boundarySkeletonOverlay.bottomPx;
      // Cue active ⇒ never render 0-height seam chrome.
      const overlayPx = seamCueForEdge(edge)
        ? Math.max(rawOverlayPx, seamPeekPx)
        : rawOverlayPx;
      const offsetTop =
        edge === 'top' ? 0 : boundarySkeletonOverlay.bottomOffsetTopPx;
      if (!seamCueForEdge(edge) || inSpacer) return null;

      const skeletonRows = seamSkeletonRowCount(overlayPx, 120);

      return (
        <div
          data-chunk-role="boundary-transcript-overlay"
          data-chunk-seam={edge}
          aria-busy="true"
          aria-label={
            edge === 'top'
              ? 'Loading earlier transcript'
              : 'Loading more transcript'
          }
          style={{
            position: 'absolute',
            top: offsetTop,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 30,
            pointerEvents: 'none',
            background: '#fff',
            height: overlayPx,
            minHeight: overlayPx,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            boxShadow:
              edge === 'bottom'
                ? 'inset 0 12px 10px -10px rgba(0,0,0,0.08)'
                : 'inset 0 -12px 10px -10px rgba(0,0,0,0.08)',
          }}
        >
          <TranscriptSkeleton count={skeletonRows} />
        </div>
      );
    };

    const renderSpacer = (height: number, position: 'before' | 'after') => {
      if (height <= 0) return null;
      const w = activeWindow;
      const intersects =
        position === 'before'
          ? liveViewport.spacers.top
          : liveViewport.spacers.bottom;
      /** Far-chunk spacer visible in viewport — skeleton placeholder instead of blank page. */
      const useSkeleton =
        !!w &&
        snapshot.index.length > 1 &&
        (intersects ||
          (viewportInLoadingZone &&
            (!!liveViewport.needsCatchUp || !editorReadyLive)));
      if (useSkeleton && w) {
        const band =
          getContentBand() ??
          windowContentBand(indexRef.current, w, heightFor);
        const skeletonHeight = Math.max(
          intersects
            ? visibleSpacerFillPx(
                position,
                height,
                liveViewport.scrollTop,
                liveViewport.clientHeight,
                band.top,
                band.bottom,
              )
            : Math.min(
                height,
                Math.max(
                  CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX,
                  liveViewport.clientHeight,
                ),
              ),
          CHUNK_SEAM_PEEK_ROWS * CHUNK_PLACEHOLDER_ROW_PX,
        );
        const maxRows = Math.min(
          48,
          Math.max(
            CHUNK_SEAM_PEEK_ROWS,
            Math.ceil(liveViewport.clientHeight / CHUNK_PLACEHOLDER_ROW_PX),
          ),
        );
        const skeletonRows = seamSkeletonRowCount(
          skeletonHeight,
          maxRows,
          position === 'before',
        );
        const loadingHint =
          chunkLoading ||
          scrollLoading ||
          wheelBlockActive ||
          !!liveViewport.needsCatchUp;
        return (
          <div
            data-chunk-spacer={position}
            data-chunk-role="loading"
            aria-busy={loadingHint ? 'true' : undefined}
            aria-label={
              position === 'before'
                ? 'Loading earlier transcript'
                : 'Loading more transcript'
            }
            style={{
              height,
              flexShrink: 0,
              overflow: 'hidden',
              background: '#fff',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: position === 'after' ? 'flex-end' : 'flex-start',
            }}
          >
            <TranscriptSkeleton count={skeletonRows} />
          </div>
        );
      }
      return (
        <div
          aria-hidden
          style={{ height, flexShrink: 0 }}
          data-chunk-spacer={position}
        />
      );
    };

    /** Sticky viewport skeleton for seek / stress paths — not at chunk seams. */
    const showViewportLoading =
      !inPostSeekGrace && chunkLoading && !boundaryChunkLoading;

    const resolvedDocument = editorDocument as CustomElement[];

    /**
     * Remount placeholder on the editor band when overlay is not covering.
     * Include far-chunk scrollbar loads — spacers alone may not fill the viewport.
     */
    const showEditorPlaceholder =
      !inPostSeekGrace &&
      editorEverReadyRef.current &&
      chunkLoading &&
      !editorReadyLive &&
      !boundaryChunkLoading &&
      !showViewportProcessingOverlay;

    const showEditorMount =
      allowEditorMount &&
      resolvedDocument.length > 0 &&
      (!showEditorPlaceholder || inPostSeekGrace);

    const editorBody = (
      <>
        {showFarChunkViewportSkeleton && (
          <div
            data-chunk-role="far-chunk-viewport-loading"
            aria-busy="true"
            aria-label="Loading transcript"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 25,
              minHeight: overlayHeight,
              pointerEvents: 'none',
              background: '#fff',
            }}
          >
            <TranscriptSkeleton
              count={Math.ceil(overlayHeight / CHUNK_PLACEHOLDER_ROW_PX)}
            />
          </div>
        )}
        {showViewportLoading &&
          !viewportInLoadingZone &&
          !showEditorPlaceholder &&
          !showViewportProcessingOverlay && (
          <div
            data-chunk-role={
              seekLoadingUi ? 'playback-seek-loading' : 'scroll-loading'
            }
            aria-busy="true"
            aria-label={
              seekLoadingUi
                ? 'Loading transcript at seek position'
                : 'Loading transcript'
            }
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              minHeight: overlayHeight,
              pointerEvents: 'none',
              background: '#fff',
            }}
          >
            <TranscriptSkeleton
              count={Math.ceil(overlayHeight / CHUNK_PLACEHOLDER_ROW_PX)}
            />
          </div>
        )}
        {showEditorMount ? (
          <DwcmBoundEditor
            slateMountKey={slateMountKey}
            windowKey={editorWindowKey}
            document={resolvedDocument}
            videoTimePosition={videoTimePosition}
            setDocument={handleSetDocument}
            interactionLocked={transcriptInteractionLocked}
            onWindowReady={finishPlaybackSeekPresentation}
          />
        ) : !seekBusy && (seamCueForEdge('top') || seamCueForEdge('bottom')) ? (
          <div
            data-chunk-role="boundary-loading"
            aria-busy="true"
            aria-label="Loading transcript"
            style={{
              position: 'relative',
              minHeight: Math.max(
                seamPeekPx,
                boundarySkeletonOverlay.topPx,
                boundarySkeletonOverlay.bottomPx +
                  boundarySkeletonOverlay.bottomOffsetTopPx,
              ),
              background: '#fff',
            }}
          />
        ) : (
          (showEditorPlaceholder || showViewportLoading) && (
          <div
            data-chunk-role="editor-placeholder"
            aria-busy="true"
            aria-label="Loading transcript editor"
            style={{ minHeight: overlayHeight, background: '#fff' }}
          >
            <TranscriptSkeleton
              count={Math.ceil(overlayHeight / CHUNK_PLACEHOLDER_ROW_PX)}
            />
          </div>
          )
        )}
      </>
    );

    if (!snapshot.index.length || snapshot.index.length <= 1) {
      return (
        <>
          {editorBody}
          <TranscriptViewportLoadingOverlay
            active={showViewportProcessingOverlay}
            waitCursor={showWaitCursor}
          />
        </>
      );
    }

    return (
      <>
      <div
        data-transcript-chunked-shell="true"
        data-dwcm-shell="true"
        data-chunk-loading={chunkLoading ? '1' : '0'}
        data-chunk-scroll-frozen={scrollFrozen ? '1' : '0'}
        data-chunk-window-start={activeWindow?.startOrdinal ?? 0}
        data-chunk-window-end={activeWindow?.endOrdinal ?? 0}
        data-boundary-trigger-top={
          boundarySkeletonOverlay.triggers.top ? '1' : '0'
        }
        data-boundary-trigger-bottom={
          boundarySkeletonOverlay.triggers.bottom ? '1' : '0'
        }
        data-boundary-skeleton-cue={
          showBoundarySkeletonCue ? '1' : '0'
        }
        data-boundary-overlay-bottom-px={boundarySkeletonOverlay.bottomPx}
        data-boundary-overlay-bottom-top-px={
          boundarySkeletonOverlay.bottomOffsetTopPx
        }
      >
        {renderSpacer(topSpacer, 'before')}
        <div
          ref={editorWindowRef}
          data-transcript-chunk-window="true"
          data-transcript-interaction-locked={
            transcriptInteractionLocked ? '1' : '0'
          }
          style={{ flexShrink: 0, position: 'relative', overflow: 'visible' }}
        >
          <div
            data-transcript-editor-band="true"
            style={{ position: 'relative', width: '100%' }}
          >
            {editorBody}
            {renderEditorSeamSkeleton('top')}
            {renderEditorSeamSkeleton('bottom')}
          </div>
        </div>
        {renderSpacer(bottomSpacer, 'after')}
      </div>
      <TranscriptViewportLoadingOverlay
        active={showViewportProcessingOverlay}
        waitCursor={showWaitCursor}
      />
      </>
    );
  },
);

DwcmChunkedEditorShell.displayName = 'DwcmChunkedEditorShell';

